import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  IonButton,
  IonContent,
  IonInput,
  IonLabel,
  IonModal,
  IonPage,
  IonPicker,
  IonPickerColumn,
  IonPickerColumnOption,
  IonSpinner,
  IonTextarea,
  useIonRouter,
} from '@ionic/react';
import { notify } from '../services/notify';
import { logDebug, logWarn } from '../services/loggerService';
import { markJustPublished } from '../utils/justPublished';
import { TopBar } from '../components/ui/TopBar';
import { useProfileStore } from '../store/useProfileStore';
import { useAuthStore } from '../store/useAuthStore';
import { useLocationStore, isSufficientLocation } from '../store/useLocationStore';
import { SignInModal } from '../components/auth/SignInModal';
import {
  savePendingListing,
  loadPendingListing,
  clearPendingListing,
  type PendingListingSnapshot,
} from '../utils/pendingListing';
import { useCreateListing, usePatchListing, useListingPatchQueue, useDeleteListing, useUploadListingImage, useGenerateListing, useListingDetail, useListings } from '../hooks/useListings';
import { useUpdateProfileAddress, useUpdateShareConnectionLevel } from '../hooks/useProfile';
import { FirstPublishVisibilityModal } from '../components/listings/FirstPublishVisibilityModal';
import { PublishInstructionsModal } from '../components/listings/PublishInstructionsModal';
import { ProfileLocationModal } from '../components/location/ProfileLocationModal';
import { ChefApplicationModal } from '../components/onboarding/ChefApplicationModal';
import { ShareLevelDropdown } from '../components/profile/ShareLevelDropdown';
import { useLocale } from '../hooks/useLocale';
import { addressesMatch, isFullProfileAddress } from '../utils/address';
import { deriveTimezone } from '../utils/timezone';
import { isRelistSource } from '../utils/listingRelist';
import { PatchListingRequest } from '../services/listingService';
import { isChef } from '../types/profile';
import { SPICINESS_LEVELS } from '../constants/tags';
import { useTagsRegistry } from '../hooks/useTagsRegistry';
import type { TagEntry } from '../services/tagsService';
import { displayTag, tagValue } from '../utils/tagLabel';

/**
 * Merge a category's preset registry with the listing's currently-stored
 * tags for THAT category. Output is the union — every preset (always
 * shown as a chip option) plus any "extras" the listing carries that
 * aren't in the preset set (e.g. AI-suggested `fusion` or a chef
 * custom-typed tag that ops never added to the curated set). The chef
 * sees and can deselect both; saves persist whatever subset is selected.
 *
 * Extras get a synthesized label (titleCase via displayTag) since they
 * aren't in the lang-aware registry. Acceptable for v1 — AI extras
 * are rare and the BE will translate them on next read of the listing.
 */
function mergeTagsForPublish(presets: TagEntry[] | undefined, selectedValues: string[]): TagEntry[] {
  const universe: TagEntry[] = presets ? [...presets] : [];
  const presetSet = new Set(universe.map((t) => t.value));
  for (const v of selectedValues) {
    if (!presetSet.has(v)) {
      universe.push({ value: v, label: displayTag(v) });
      presetSet.add(v);
    }
  }
  return universe;
}
import { ImageLibraryModal } from '../components/listings/ImageLibraryModal';
import { SelectableChip } from '../components/ui/SelectableChip';
import { CancelListingModal } from '../components/listings/CancelListingModal';
import { LockedFieldOverlay } from '../components/listings/LockedFieldOverlay';
import { EditLockedModal } from '../components/listings/EditLockedModal';
import { useListingOrders } from '../hooks/useOrders';
import { LocationPicker, LocationPickerValue, LocationPickerHandle, buildProfileLocation, buildListingLocation } from '../components/location/LocationPicker';
import { Check, Locate } from 'lucide-react';
import {
  buildDefaultWindow,
  buildDropoffWindowAfter,
  clampWindow,
  compareWindowStart,
  windowStartingAfter,
  DROPOFF_DURATION_BOUNDS,
  PICKUP_DURATION_BOUNDS,
  getEndDate,
  getEndTime,
  normalizeWindowState,
  fromTimeWindow,
  windowsOverlap,
  type DurationBounds,
  type OriginalTimeWindow,
  type TimeWindowState,
} from '../utils/listingTimeWindow';
import { HandoffWindowCard, type HandoffKind } from '../components/listings/HandoffWindowCard';

// Service key (scope: listing_generate) that lets a logged-out user run the
// AI generate endpoint's stateless path. Empty/unset → logged-out AI gen is
// simply skipped (e.g. an env where no key has been minted yet).
const GENERATE_SERVICE_KEY = import.meta.env.VITE_GENERATE_SERVICE_KEY as string | undefined;

/* ---- Isolated picker sub-components ----
   Each is memo'd so a state change in one picker doesn't re-render the other.
   Ionic's IonPickerColumn fires spurious onIonChange events during re-render,
   which was causing the "ghost scroll" bug when both pickers shared a parent. */

const PricePicker = memo(function PricePicker({
  value,
  onChange,
  freeLabel,
  maxDollars,
}: {
  value: number;
  onChange: (val: number | string) => void;
  /** Translated label for the "Free" option ($0). Threaded in from the
   *  parent so the picker stays free of i18n bindings inside `memo()`. */
  freeLabel: string;
  /** Locale-provided price ceiling in whole dollars. The wheel always offers
   *  $1..$12; prices above the cap render grayed-out and, when settled on,
   *  bounce back to the cap with a "not available in your area" explainer.
   *  maxDollars = 0 grays the entire $1..$12 range (Free-only locale). */
  maxDollars: number;
}) {
  const elRef = useRef<HTMLIonPickerColumnElement>(null);
  // Highest in-cap price ($1..capDollars are selectable; capDollars+1..12 are
  // grayed). The ★ option and any grayed over-cap price both resolve here, so
  // neither can push the listing above the locale cap. capDollars = 0 grays
  // the whole $1..$12 range, leaving only Free ($0) selectable.
  const capDollars = Math.min(Math.max(maxDollars, 0), 12);
  // Dedup the raw `v` (string sentinel or number) — never the resolved
  // numeric, since 'blue_star_chef' resolves to capDollars and conflating
  // them swallows the very landing event we need to trigger the explainer.
  const ref = useRef<number | string>(value);
  // Keep ref in sync with prop-driven snaps (parent setState that visually
  // moves the wheel without firing onIonChange). Without this, the dedup
  // check below swallows the user's first scroll if it happens to land on
  // the initial-render value the ref was frozen to.
  useEffect(() => { ref.current = value; }, [value]);
  return (
    <IonPicker>
      <IonPickerColumn
        ref={elRef}
        value={value}
        onIonChange={(e) => {
          const v = e.detail.value;
          if (v == null) return;
          if (v === ref.current) return;
          ref.current = v;
          onChange(v);
          // The ★ entry and any grayed over-cap price both bounce back to the
          // cap. onIonChange fires on scroll-settle (not mid-scroll), so this
          // only triggers when the user actually rests on one. Force the wheel
          // back to capDollars; React state can't drive the re-snap because
          // `price` is already capDollars (no @Watch fire). Deferred to the
          // next microtask so we don't fight the scroll-settle cycle that just
          // emitted this event.
          const overCap = typeof v === 'number' && v > capDollars;
          if (v === 'blue_star_chef' || overCap) {
            queueMicrotask(() => {
              ref.current = capDollars;
              elRef.current?.setValue(capDollars);
            });
          }
        }}
      >
        <IonPickerColumnOption value={0}>{freeLabel}</IonPickerColumnOption>
        {Array.from({ length: 12 }, (_, i) => i + 1).map((v) => (
          <IonPickerColumnOption key={v} value={v}>
            {/* Grayed (but still settle-able) when above the locale cap — the
                parent shows the "not available in your area yet" explainer.
                The color goes on this slotted span, not the option host: the
                option button is color:inherit off an Ionic-themed host, so a
                host className gets overridden — an explicit span color wins. */}
            <span className={v > capDollars ? 'text-muted-foreground opacity-40' : undefined}>${v}</span>
          </IonPickerColumnOption>
        ))}
        {/* ★ (Blue Star Chef) always sits at the bottom of the wheel as the
            explainer entry, whatever the cap. It resolves to capDollars (the
            highest in-cap price) so selecting it never exceeds the locale cap. */}
        <IonPickerColumnOption value="blue_star_chef">&#9733;</IonPickerColumnOption>
      </IonPickerColumn>
    </IonPicker>
  );
});

const QuantityPicker = memo(function QuantityPicker({
  value,
  onChange,
  min = 1,
}: {
  value: number;
  onChange: (val: number | string) => void;
  /** Floor for the picker — values below this are removed from the wheel.
   *  Used in "edit listing with orders" mode to prevent lowering portions
   *  below the count already sold. */
  min?: number;
}) {
  const elRef = useRef<HTMLIonPickerColumnElement>(null);
  // Dedup the raw `v` (string sentinel or number) — never the resolved
  // numeric, since 'feed_community' resolves to 10 and conflating them
  // swallows the very landing event we need to trigger the explainer.
  const ref = useRef<number | string>(value);
  // Keep ref in sync with prop-driven snaps. See PricePicker for rationale.
  useEffect(() => { ref.current = value; }, [value]);
  const floor = Math.max(1, Math.floor(min));
  const top = Math.max(floor, 10);
  const snapBack = Math.max(10, floor);
  return (
    <IonPicker>
      <IonPickerColumn
        ref={elRef}
        value={value}
        onIonChange={(e) => {
          const v = e.detail.value;
          if (v == null) return;
          if (v === ref.current) return;
          ref.current = v;
          onChange(v);
          if (v === 'feed_community') {
            // Force the wheel back to the clamped numeric. See PricePicker
            // for the full explanation — Ionic's internal value just became
            // 'feed_community' and React state can't drive a re-snap when
            // `quantity` is already at the snap-back value.
            queueMicrotask(() => {
              ref.current = snapBack;
              elRef.current?.setValue(snapBack);
            });
          }
        }}
      >
        {Array.from({ length: top - floor + 1 }, (_, i) => i + floor).map((v) => (
          <IonPickerColumnOption key={v} value={v}>{v}</IonPickerColumnOption>
        ))}
        <IonPickerColumnOption value="feed_community">&#127858;</IonPickerColumnOption>
      </IonPickerColumn>
    </IonPicker>
  );
});

/**
 * One-column wheel over a fixed option list. Used for the drop-off radius and
 * fee, both of which are short enumerations rather than the open ranges
 * Price/Quantity deal with. memo'd and dedup-ref'd for the same reason as the
 * pickers above: IonPickerColumn fires spurious onIonChange during re-render.
 */
const OptionWheel = memo(function OptionWheel({
  value,
  options,
  onChange,
  suffix,
}: {
  value: number;
  options: { value: number; label: string }[];
  onChange: (val: number) => void;
  /** Translated column suffix, threaded in so this stays free of i18n. */
  suffix?: string;
}) {
  const ref = useRef(value);
  useEffect(() => { ref.current = value; }, [value]);
  return (
    <IonPicker>
      <IonPickerColumn
        value={value}
        onIonChange={(e) => {
          const v = e.detail.value;
          if (typeof v !== 'number' || v === ref.current) return;
          ref.current = v;
          onChange(v);
        }}
      >
        {suffix ? <div slot="suffix" className="text-sm text-gray-500">{suffix}</div> : null}
        {options.map((o) => (
          <IonPickerColumnOption key={o.value} value={o.value}>{o.label}</IonPickerColumnOption>
        ))}
      </IonPickerColumn>
    </IonPicker>
  );
});

// Miles the chef will drive. Mirrors model.MinDropoffRadiusMiles /
// MaxDropoffRadiusMiles on the backend, which rejects anything outside it.
// 1 to 5 miles in half-mile steps.
const DROPOFF_RADIUS_OPTIONS = Array.from({ length: 9 }, (_, i) => 1 + i * 0.5);
const DEFAULT_DROPOFF_RADIUS = 2;
// Half miles read as "1½" on the wheel rather than "1.5".
function formatRadiusLabel(miles: number): string {
  const whole = Math.floor(miles);
  return miles % 1 === 0 ? String(whole) : `${whole}½`;
}
// Snaps a saved radius onto the wheel, so a draft from before the 5 mi cap
// doesn't leave the picker on a value it can't show.
function snapDropoffRadius(miles: number | null | undefined): number {
  if (miles == null) return DEFAULT_DROPOFF_RADIUS;
  const snapped = Math.round(miles * 2) / 2;
  return Math.min(Math.max(snapped, DROPOFF_RADIUS_OPTIONS[0]), DROPOFF_RADIUS_OPTIONS[DROPOFF_RADIUS_OPTIONS.length - 1]);
}
// Whole dollars, matching how the listing price is picked. 0 reads as "Free".
const DROPOFF_FEE_OPTIONS = Array.from({ length: 11 }, (_, i) => i);

export const PublishListingPage: React.FC = () => {
  const { t, i18n } = useTranslation(['publish', 'profile']);
  const history = useHistory();
  const ionRouter = useIonRouter();
  const routerLocation = useLocation();
  const { id: editId } = useParams<{ id?: string }>();
  const activeProfile = useProfileStore((s) => s.activeProfile);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  // The user's own location (distinct from the dish `location` state below).
  // Gates logged-out AI generation: only call the public generate endpoint
  // once the user has set a location — the same precondition as the publish CTA.
  const userLocation = useLocationStore((s) => s.location);
  // The user's current operating locale (the TopBar pill) and a setter to
  // switch it — used to warn when the listing's address sits outside it.
  const currentLocaleId = useLocationStore((s) => s.localeId);
  const setLocale = useLocationStore((s) => s.setLocale);
  const updateProfileAddress = useUpdateProfileAddress();

  // Eaters are allowed all the way through the publish form now — the chef
  // requirement is enforced at the final "Publish" step via the chef-quiz gate
  // in handleSave (an eater takes the quick chef quiz, then the publish
  // resumes). Chefs skip the gate. No up-front role redirect.

  // Logged-out publish: the in-progress dish is buffered to localStorage so it
  // survives a reload. Read it ONCE at init (only for a new listing while
  // logged out) so the useState/accumulatedFields below can seed from it on the
  // very first render — that way the LocationPicker sees a non-null value and
  // won't auto-pick over the restored location. See utils/pendingListing.
  const initialPendingRef = useRef<ReturnType<typeof loadPendingListing> | undefined>(undefined);
  if (initialPendingRef.current === undefined) {
    initialPendingRef.current = !isAuthenticated && !editId ? loadPendingListing() : null;
  }
  const initialPending = initialPendingRef.current;

  // Safety-net warn: the logged-out AI-generate flow requires
  // VITE_GENERATE_SERVICE_KEY at build time (see canGenerateLoggedOut
  // below). If the deploy pipeline drops the secret again, all logged-out
  // /publish-listing sessions go silent — this warn is the breadcrumb.
  // Fires once per mount for unauth visitors on a build with no key.
  useEffect(() => {
    if (!activeProfile && !import.meta.env.VITE_GENERATE_SERVICE_KEY) {
      logWarn(
        'Logged-out publish flow disabled: VITE_GENERATE_SERVICE_KEY is not set in this build',
      );
    }
  }, [activeProfile]);

  // Editing an existing listing is owner-only — bounce a logged-out visitor who
  // somehow lands on /publish-listing/:id. (New-listing entry has no id and is
  // public.)
  useEffect(() => {
    if (editId && !isAuthenticated) history.replace('/');
  }, [editId, isAuthenticated, history]);

  const listingId = useRef<string | null>(editId ?? null);
  const { data: existingListing, isLoading: isDraftLoading } = useListingDetail(editId);

  // ?copy=1 forces the form into relist mode even on a still-active listing,
  // used when the lister taps a locked field and chooses to make a copy.
  // Same-route navigation, so the URL is the only signal that survives reload.
  const isCopyParam = useMemo(
    () => new URLSearchParams(routerLocation.search).has('copy'),
    [routerLocation.search],
  );

  // Relist ("Duplicate") mode: the URL points at an existing listing, but it
  // can no longer be edited. We pre-fill the form from the source and create a
  // brand-new listing on save (see the seed effect below).
  //
  // isRelistSource covers retired statuses + past windows — the same check
  // ListingDetailsPage uses to label its button "Duplicate" instead of "Edit",
  // so the affordance and the behavior can't drift apart. ?copy=1 adds the
  // third entry: an explicit user request from the lock prompt on a listing
  // that IS still editable but has orders on it.
  const isRelisting = !!existingListing && (isRelistSource(existingListing) || isCopyParam);
  const isEditMode = !!editId && !isRelisting;

  // Live orders on the listing under edit (paid+ statuses only — held/confirmed
  // aren't returned by this endpoint). Sum of non-canceled order quantities is
  // the same math the backend uses for `itemsRemaining`, so it's the correct
  // floor for lowering the listing's quantity.
  const { data: listingOrders } = useListingOrders(isEditMode ? editId : undefined);
  const portionsSold = useMemo(() => {
    if (!listingOrders) return 0;
    return listingOrders
      .filter((o) => o.status !== 'canceled' && o.status !== 'expired')
      .reduce((sum, o) => sum + (o.quantity ?? 0), 0);
  }, [listingOrders]);
  const activeOrderCount = useMemo(() => {
    if (!listingOrders) return 0;
    return listingOrders.filter((o) => o.status !== 'canceled' && o.status !== 'expired').length;
  }, [listingOrders]);
  const isEditWithOrders = isEditMode && portionsSold > 0;

  // Resume-Draft swaps the URL from /publish-listing to /publish-listing/{id}
  // without unmounting (same Route). useRef's initial value is one-shot, so
  // sync editId → listingId.current whenever the param appears.
  //
  // Skip the sync once we know we're relisting — the seed effect intentionally
  // nulls out listingId.current to force a fresh POST on save. Without this
  // guard, StrictMode's effect double-invocation re-runs this sync after the
  // seed clears, re-pointing the ref at the old (uneditable) listing, and the
  // LocationPicker's auto-pick onChange then PATCHes the old listing.
  useEffect(() => {
    if (editId && !isRelisting) listingId.current = editId;
  }, [editId, isRelisting]);

  const createMutation = useCreateListing();
  // Every PATCH this form sends for its own listing goes through this queue,
  // one at a time (see useListingPatchQueue): parallel autosaves refused each
  // other as stale on the server and could land out of order.
  const patchQueue = useListingPatchQueue();
  // Separate mutation instance so the cancel modal's spinner reflects ONLY
  // the cancel request, not in-flight auto-save patches on the same page.
  const cancelMutation = usePatchListing();
  const deleteMutation = useDeleteListing();
  const generateMutation = useGenerateListing();
  const isSaving = createMutation.isPending || patchQueue.isPending;

  const uploadImageMutation = useUploadListingImage();
  const [selectedImageUrl, setSelectedImageUrl] = useState<string | null>(initialPending?.imageUrl ?? null);
  const [selectedThumbnailUrl, setSelectedThumbnailUrl] = useState<string | null>(initialPending?.thumbnailUrl ?? null);
  // Once the user picks their own image (upload or preset library), the AI must
  // never overwrite it. Latched true on any manual choice and checked before
  // requesting/applying an AI-picked image.
  const userChoseImageRef = useRef(!!initialPending?.imageUrl);
  const [showImageLibrary, setShowImageLibrary] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  // Lock-prompt flow: tapping a locked field opens this; choosing
  // "delete and copy" then opens the cancel modal in copy-then-redirect mode.
  const [showEditLockedModal, setShowEditLockedModal] = useState(false);
  const [editLockedReason, setEditLockedReason] = useState<'orders' | 'methods'>('orders');
  const [cancelMode, setCancelMode] = useState<'plain' | 'cancel-and-copy'>('plain');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isEditingDraft = isEditMode && existingListing?.status === 'draft';
  // How food changes hands is fixed once published, orders or not: eaters
  // ordered against it and a drop-off run may be planned around it. Window
  // times stay editable until there are orders (isEditWithOrders); the method
  // set never is. The backend refuses the change too (LISTING_METHODS_LOCKED).
  const handoffMethodsLocked = isEditMode && !isEditingDraft;
  // True whenever the listing currently on the server is a draft — covers
  // both opening an existing draft (isEditingDraft) and the new-listing
  // flow once title blur auto-created a draft. Drives the Cancel→Delete
  // swap. Re-renders correctly because createMutation state changes after
  // ensureCreated sets listingId.current.
  const isOperatingOnDraft =
    isEditingDraft || (!isEditMode && !!listingId.current);

  // Resume-draft banner: when entering /publish-listing with no id, surface
  // the user's most recent draft so they can pick up where they left off.
  // Drafts the user already dismissed are filtered out so the prompt doesn't
  // come back for that specific draft.
  const { data: myListings } = useListings(isAuthenticated);
  const [draftBannerDismissed, setDraftBannerDismissed] = useState(false);
  const patchListingMutation = usePatchListing();
  const resumableDraft = !isEditMode && myListings
    ? [...myListings]
        .filter((l) => l.status === 'draft' && !l.draftBannerDismissed)
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0]
    : undefined;

  function handleDismissDraftBanner() {
    setDraftBannerDismissed(true);
    if (resumableDraft) {
      patchListingMutation.mutate({
        id: resumableDraft.id,
        fields: { draftBannerDismissed: true },
      });
    }
  }

  const canCancelListing =
    isEditMode &&
    !!existingListing &&
    (existingListing.status === 'draft' ||
      existingListing.status === 'scheduled' ||
      existingListing.status === 'active');

  function handleConfirmCancel(reason: string) {
    const id = listingId.current;
    if (!id) return;
    setCancelError(null);
    cancelMutation.mutate(
      { id, fields: { status: 'canceled', cancellationReason: reason } },
      {
        onSuccess: () => {
          setShowCancelModal(false);
          if (cancelMode === 'cancel-and-copy') {
            // Stay on the page; re-seed the form as a copy of the now-canceled
            // listing. URL flips to ?copy=1 so reload preserves intent and
            // isRelisting recomputes to true.
            seedAsCopy();
            setCancelMode('plain');
            history.replace(`/publish-listing/${id}?copy=1`);
          } else {
            history.replace('/my-kitchen');
          }
        },
        onError: (e) => {
          console.error('[PublishListing] cancel failed', e);
          setCancelError(
            e instanceof Error ? e.message : t('errors.cancelFailed'),
          );
        },
      },
    );
  }

  function handleCloseCancelModal() {
    setShowCancelModal(false);
    setCancelError(null);
    setCancelMode('plain');
  }

  // Draft-deletion path. Distinct from cancel because:
  //   • drafts hit DELETE /listings/{id} (the backend rejects DELETE on
  //     anything past draft status) — true row removal, not a status flip,
  //   • the auto-draft created on title-blur in new-listing flow would
  //     otherwise pile up as status='canceled' rows.
  function handleConfirmDelete() {
    const id = listingId.current;
    if (!id) return;
    setDeleteError(null);
    deleteMutation.mutate(id, {
      onSuccess: () => {
        setShowDeleteModal(false);
        history.replace('/my-kitchen');
      },
      onError: (e) => {
        console.error('[PublishListing] delete failed', e);
        setDeleteError(e instanceof Error ? e.message : t('errors.deleteFailed'));
      },
    });
  }

  function handleCloseDeleteModal() {
    setShowDeleteModal(false);
    setDeleteError(null);
  }

  /**
   * Re-seed the form as a copy of `existingListing`, in place. Mirrors the
   * relist branch of the seed effect (which only runs once on mount). Used by
   * the lock-prompt flow when the user is already on /publish-listing/{id} and
   * chooses to start a copy — the component doesn't unmount on same-route
   * navigation, so the seed effect won't fire again on its own.
   */
  function seedAsCopy() {
    if (!existingListing) return;
    listingId.current = null;
    originalWindow.current = { pickup: undefined, dropoff: undefined };
    accumulatedFields.current = {
      description: existingListing.description ?? undefined,
      // Wire-format values only — see the note in the seed effect's relist branch.
      dietaryTags: existingListing.dietaryTags?.map(tagValue) ?? undefined,
      originTags: existingListing.originTags?.map(tagValue) ?? undefined,
      allergenTags: existingListing.allergenTags?.map(tagValue) ?? undefined,
      price: existingListing.price ?? 0,
      quantity: existingListing.quantity ?? 1,
      fulfillmentMethod: existingListing.fulfillmentMethod ?? [],
      imageUrl: existingListing.imageUrl ?? undefined,
      thumbnailUrl: existingListing.thumbnailUrl ?? undefined,
      ingredients: existingListing.ingredients ?? undefined,
    };
    const copiedMethods = existingListing.fulfillmentMethod ?? [];
    setFulfillment(copiedMethods);
    const seeded = defaultWindowsFor(copiedMethods);
    commitWindowsToAccumulated(
      seeded.pickup,
      seeded.dropoff,
      copiedMethods,
      seedDropoffSettings(existingListing),
    );
    setLocation(null);
    setLocationReady(false);
  }

  function handleLockedTap() {
    setEditLockedReason('orders');
    setShowEditLockedModal(true);
  }

  // The pickup/drop-off lock applies from publish, orders or not, so its
  // prompt can't say "with orders on it".
  function handleMethodsLockedTap() {
    setEditLockedReason('methods');
    setShowEditLockedModal(true);
  }

  function handleCopyKeepActive() {
    if (!editId) return;
    setShowEditLockedModal(false);
    seedAsCopy();
    history.replace(`/publish-listing/${editId}?copy=1`);
  }

  function handleDeleteAndCopy() {
    setShowEditLockedModal(false);
    setCancelMode('cancel-and-copy');
    setShowCancelModal(true);
  }

  // --- AI generation state ---
  type AiField = 'description' | 'ingredients' | 'tags' | 'image';
  const [aiGenerating, setAiGenerating] = useState<Set<AiField>>(new Set());
  const aiAbortRef = useRef<AbortController | null>(null);
  const AI_GENERATION_TIMEOUT_MS = 45_000;

  // Stuck-state watchdog. Each AI call already aborts on its own timeout, but a
  // race between two overlapping triggerGenerateFrom runs (they share one
  // aiAbortRef and both mutate `aiGenerating`) could leave the set non-empty
  // after all work has settled — silently disabling Publish forever. This is a
  // belt-and-suspenders backstop: whenever the set is non-empty, arm a timer to
  // force-clear it. The ceiling is well past the worst legitimate run (two
  // sequential calls, each capped at AI_GENERATION_TIMEOUT_MS) so it only ever
  // rescues a genuinely stuck set, never interrupts a real generation.
  const AI_STUCK_GUARD_MS = AI_GENERATION_TIMEOUT_MS * 2 + 10_000;
  useEffect(() => {
    if (aiGenerating.size === 0) return;
    const id = setTimeout(() => {
      console.warn('[PublishListing] AI generation watchdog fired — force-clearing stuck aiGenerating set');
      aiAbortRef.current?.abort();
      aiAbortRef.current = null;
      setAiGenerating(new Set());
    }, AI_STUCK_GUARD_MS);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiGenerating]);

  // BE tag registry — feeds the chip universe for the 3 category sections.
  // Single source of truth for "what chips to show as default options".
  // Extras (tags on the listing that aren't in the preset registry, e.g.
  // AI-emitted "fusion") get rendered too via `mergeTagsForPublish`.
  const { data: tagsRegistry } = useTagsRegistry();

  // --- Form state ---
  // Seeds from a buffered logged-out dish (`initialPending`) when present, else
  // the normal empty defaults.
  const [title, setTitle] = useState(initialPending?.title ?? '');
  const [description, setDescription] = useState(initialPending?.description ?? '');
  const [spiciness, setSpiciness] = useState(initialPending?.spiciness ?? 0);
  const [dietaryTags, setDietaryTags] = useState<string[]>(initialPending?.dietaryTags ?? []);
  const [originTags, setOriginTags] = useState<string[]>(initialPending?.originTags ?? []);
  const [allergenTags, setAllergenTags] = useState<string[]>(initialPending?.allergenTags ?? []);
  const [price, setPrice] = useState<number>(initialPending?.price ?? 0);
  const [quantity, setQuantity] = useState<number>(initialPending?.quantity ?? 1);
  // Per-dish audience reach. Seeds from the listing's stamped value (or the
  // chef's profile default for a fresh draft). 1 = friends & family, 2 = FoF,
  // 3 = anyone.
  const [shareLevel, setShareLevel] = useState<number>(initialPending?.shareLevel ?? 3);
  const [showBlueStarSheet, setShowBlueStarSheet] = useState(false);
  const [showLockedPriceSheet, setShowLockedPriceSheet] = useState(false);
  const [showFeedCommunitySheet, setShowFeedCommunitySheet] = useState(false);
  // First-time listers get a one-time nudge toward free dishes when they pick a
  // paid price. The ref makes it fire once per publish session (not on every
  // wheel tick, and not again if they toggle free→paid→free→paid).
  const [showFirstPriceNudge, setShowFirstPriceNudge] = useState(false);
  const firstPriceNudgeShownRef = useRef(false);
  // Empty by default: a new listing must not assume pickup. Publish stays
  // blocked (publishBlockers.pickHandoff) until the chef picks one or both.
  const [fulfillment, setFulfillment] = useState<string[]>(initialPending?.fulfillment ?? []);
  const [location, setLocation] = useState<LocationPickerValue | null>(initialPending?.location ?? null);
  const locationPickerRef = useRef<LocationPickerHandle>(null);
  const [geoLoading, setGeoLoading] = useState(false);
  const [geoError, setGeoError] = useState<string | null>(null);
  // Tracks whether the apt/unit prompt is resolved (entered OR explicitly None).
  // Defaults true so we don't block publish until LocationPicker has a real
  // address to evaluate — it'll flip to false the moment an address with no
  // unit is picked.
  const [aptResolved, setAptResolved] = useState(true);
  const [pinResolved, setPinResolved] = useState(true);
  // Per-publish acknowledgement that the user wants to publish with a no-street
  // listing location (pin or city centroid). NOT persisted — re-asked every
  // publish flow since the consequences are per-listing. Resets whenever the
  // location's coords change so a new pin requires fresh acknowledgement.
  const [listingPinAcknowledged, setListingPinAcknowledged] = useState(false);
  useEffect(() => {
    setListingPinAcknowledged(false);
  }, [location?.address.lat, location?.address.lon]);

  const handleUseCurrentLocation = async () => {
    setGeoError(null);
    setGeoLoading(true);
    try {
      await locationPickerRef.current?.useCurrentLocation();
    } catch (err) {
      setGeoError(err instanceof Error ? err.message : t('location.errorGeneric'));
    } finally {
      setGeoLoading(false);
    }
  };

  // date / startTime / durationMin always have values; defaults are produced by
  // buildDefaultWindow. In edit mode the defaults get replaced from the loaded
  // listing in the seed effect below, before the user can interact.
  // Keyed by hand-off kind: the two windows save independently, so a drop-off
  // edit landing while a pickup save is still in flight must not revert or
  // supersede it.
  const originalWindow = useRef<Record<HandoffKind, OriginalTimeWindow | undefined>>({
    pickup: undefined,
    dropoff: undefined,
  });
  // The window value the backend last confirmed (seeded on edit-load, updated
  // after every successful save). A failed save reverts the controls to this so
  // the UI can never silently lead the persisted value. `windowSaveSeq` tags
  // each in-flight save so a stale response can't clobber a newer edit.
  const lastSavedWindow = useRef<Record<HandoffKind, TimeWindowState | null>>({
    pickup: null,
    dropoff: null,
  });
  const windowSaveSeq = useRef<Record<HandoffKind, number>>({ pickup: 0, dropoff: 0 });
  // A restored buffer's window can be stale (e.g. resumed the next day), so
  // clamp it forward to the valid minimum rather than seeding a past window.
  const [pickupWindow, setPickupWindow] = useState<TimeWindowState>(() => {
    // normalizeWindowState: the buffer is unversioned, so one written before
    // the window carried a duration still restores.
    const restored = normalizeWindowState(initialPending?.timeWindow);
    return restored ? clampWindow(restored, undefined) : buildDefaultWindow();
  });
  // Null until the chef checks Drop-off, and null is load-bearing rather than
  // just "unset": it's what makes the wire layer send `{}` and clear whatever
  // drop-off window the listing previously stored.
  const [dropoffWindow, setDropoffWindow] = useState<TimeWindowState | null>(() => {
    const restored = normalizeWindowState(initialPending?.dropoffWindow);
    return restored ? clampWindow(restored, undefined, DROPOFF_DURATION_BOUNDS) : null;
  });

  // Drop-off run settings. Held in miles / whole DOLLARS here; the wire format
  // is miles / CENTS, converted at the staging boundary below.
  const [dropoffRadius, setDropoffRadius] = useState<number>(
    snapDropoffRadius(initialPending?.dropoffRadius),
  );
  const [dropoffFeeDollars, setDropoffFeeDollars] = useState<number>(
    initialPending?.dropoffFeeDollars ?? 0,
  );

  const offersPickup = fulfillment.includes('pickup');
  const offersDropoff = fulfillment.includes('dropoff');

  // --- Publish validation ---
  // The required-field blockers only apply when the action would publish
  // (new listing or editing draft); updating an already-published listing
  // skips those. The dish-locale blocker applies in both modes — moving a
  // published listing to an unsupported area shouldn't be allowed either.
  const isPublishingAttempt = !isEditMode || !!isEditingDraft;

  // Resolve the *listing's* location to a locale (separate from the user's
  // own locale in useLocationStore). Only dish-type locales are valid hosts;
  // postalCode/state_province/ambassador locales are unsupported areas.
  const listingLat = location?.address.lat;
  const listingLon = location?.address.lon;
  // The timezone the listing should be stamped with. Derived from the
  // listing's own coordinates (offline lat/lon → IANA lookup) so it reflects
  // where the listing IS, not where the publisher's browser happens to be.
  // Falls back to the browser zone only while the listing has no location yet
  // (e.g. the on-mount default seed, before auto-pick/first address fires).
  const listingTimezone = useMemo(
    () =>
      deriveTimezone(listingLat, listingLon) ??
      Intl.DateTimeFormat().resolvedOptions().timeZone,
    [listingLat, listingLon],
  );
  const { data: listingLocale, isError: listingLocaleFailed } = useLocale(
    listingLat != null && listingLon != null ? { lat: listingLat, lon: listingLon } : null,
  );

  // The listing has coordinates but we haven't resolved their locale yet.
  // Every locale-derived guard below (price cap, supported-area, cross-locale)
  // reads `false`/`undefined` in this state, so without an explicit gate the
  // Publish button is at its MOST permissive exactly when we know the least.
  // That's the window that breaks "duplicate, then move the dish": the price
  // is still the source listing's, the location is already the new one, and
  // the BE rejects the pair with LISTING_PRICE_OVER_LOCALE_CAP.
  //
  // Only blocks while the lookup is genuinely in flight. A locale query that
  // errors out settles into `isError` and stops blocking — we'd rather let the
  // chef try and surface the BE's verdict than wedge the button on a failed
  // lookup they can't retry.
  const listingLocaleUnresolved =
    listingLat != null && listingLon != null && !listingLocale && !listingLocaleFailed;
  const listingLocationOutsideDishLocale =
    !!listingLocale && listingLocale.type !== 'dish';

  // The listing's address resolves to a *dish* locale other than the user's
  // current operating locale. Publishing here is allowed (it's a supported
  // area), but we offer a one-tap switch so the chef's feed/context follows
  // the listing. Only meaningful when the user actually has a current locale
  // to be "outside" of.
  const listingInOtherDishLocale = !!(
    listingLocale &&
    listingLocale.type === 'dish' &&
    currentLocaleId &&
    listingLocale.id !== currentLocaleId
  );

  // Locale-provided price ceiling (CENTS; 0 = Free-only). While the listing's
  // locale is still resolving we don't know the cap yet, so keep the historical
  // $12 ceiling until it loads, then constrain the wheel to the locale's value.
  const priceCapCents = listingLocale?.maxPrice;
  const maxPriceDollars = priceCapCents != null ? Math.floor(priceCapCents / 100) : 12;
  // Highest real price the wheel actually offers ($1..wheelTopDollars). The ★
  // option resolves to this, so selecting it never exceeds the locale cap.
  const wheelTopDollars = Math.min(Math.max(maxPriceDollars, 0), 12);

  // Keep the selected price within the locale's cap. Fires when the cap
  // resolves after a selection, or in edit mode on a listing whose locale cap
  // was since lowered. Fresh picks are already limited by the wheel's options;
  // this pulls an already-set price down so state + payload never exceed the
  // cap (and the publish-time safety blocker below stays a no-op in practice).
  useEffect(() => {
    if (priceCapCents == null) return; // cap unknown yet — don't touch
    if (price * 100 > priceCapCents) {
      const clamped = Math.floor(priceCapCents / 100);
      setPrice(clamped);
      savePatch({ price: clamped * 100 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priceCapCents, price]);

  // US-only — block publishing when LocationPicker returns a non-US country.
  // Mirrors the BE guard in handler/listing.go (defense in depth so the BE
  // round-trip never has to reject a normal user-driven publish).
  const listingCountryNotUS = !!(
    location?.address.country && location.address.country !== 'US'
  );

  // A valid listing location needs lat/lon + city/state/postal/country for
  // tax & feed queries. Either a real street1 OR an explicitly-confirmed
  // pin location satisfies the "we know where this is" requirement.
  const hasValidListingLocation = !!(
    (location?.address.street1 || location?.address.pinConfirmed) &&
    location?.address.city &&
    location.address.stateProvince &&
    location.address.postalCode &&
    location.address.country &&
    location.address.lat != null &&
    location.address.lon != null
  );

  // No-street listing location requires per-publish acknowledgement even after
  // the address-level `pinConfirmed` flag is set. We want the user to actively
  // re-confirm each time they publish from a non-addressable location.
  const needsListingPinAck = !!(
    location &&
    !location.address.street1 &&
    location.address.lat != null &&
    location.address.lon != null
  );

  const publishBlockers = useMemo(() => {
    const blockers: string[] = [];
    if (isPublishingAttempt) {
      if (!title.trim()) blockers.push(t('publishBlockers.addTitle'));
      if (price == null) blockers.push(t('publishBlockers.setPrice'));
      // Safety net for the locale price cap — the price wheel already hides
      // over-cap options, so this should never fire in practice.
      else if (priceCapCents != null && price * 100 > priceCapCents)
        blockers.push(t('publishBlockers.priceTooHigh'));
      if (quantity == null) blockers.push(t('publishBlockers.setQuantity'));
      if (fulfillment.length === 0) blockers.push(t('publishBlockers.pickHandoff'));
      if (offersPickup && !pickupWindow) blockers.push(t('publishBlockers.pickWindow'));
      if (offersDropoff && !dropoffWindow) {
        blockers.push(t('publishBlockers.pickDropoffWindow'));
      }
      // Backstop: the wheel is seeded with a default, so this only fires if the
      // value is somehow cleared. The server rejects the same case at publish.
      if (offersDropoff && !dropoffRadius) {
        blockers.push(t('publishBlockers.setDropoffRadius'));
      }
      // Mirrors the backend's publish gate so the chef is told before they
      // submit rather than by a 400. The server stays the authority.
      if (offersPickup && offersDropoff && dropoffWindow &&
          windowsOverlap(pickupWindow, dropoffWindow)) {
        blockers.push(t('publishBlockers.windowsOverlap'));
      }
      if (!aptResolved) blockers.push(t('publishBlockers.confirmApt'));
    }
    if (!pinResolved) {
      blockers.push(t('publishBlockers.confirmPin'));
    } else if (!hasValidListingLocation) {
      blockers.push(t('publishBlockers.addLocation'));
    } else if (needsListingPinAck && !listingPinAcknowledged) {
      blockers.push(t('publishBlockers.confirmPinForListing'));
    }
    if (listingCountryNotUS) {
      // US-only takes precedence over the in-supported-area message —
      // a chef in Toronto isn't going to pick a "supported area" inside Canada.
      blockers.push(t('publishBlockers.usOnly'));
    } else if (listingLocationOutsideDishLocale) {
      blockers.push(t('publishBlockers.pickSupportedArea'));
    }
    // Block cross-locale publishing: a listing must be published from the
    // locale the user is currently operating in (no cross-locale
    // contamination). Switching via the warning-box button clears this.
    if (listingInOtherDishLocale) {
      blockers.push(t('publishBlockers.switchLocale'));
    }
    return blockers;
  }, [t, isPublishingAttempt, title, price, priceCapCents, quantity, pickupWindow,
    dropoffWindow, dropoffRadius, fulfillment, offersPickup, offersDropoff, aptResolved, pinResolved, hasValidListingLocation, needsListingPinAck, listingPinAcknowledged, listingLocationOutsideDishLocale, listingInOtherDishLocale, listingCountryNotUS]);

  // Single source of truth for "why is Publish disabled?". The button is
  // disabled iff this list is non-empty OR a publish is already in flight
  // (which shows a spinner in the button). Every non-spinner disabler MUST
  // contribute a reason here so the button can never gray out silently —
  // publishBlockers plus the transient states (AI generation / saving) that
  // previously disabled the button with no on-screen explanation.
  const disableReasons = useMemo(() => {
    const reasons = [...publishBlockers];
    if (aiGenerating.size > 0) reasons.push(t('publishBlockers.aiGenerating'));
    if (isSaving) reasons.push(t('publishBlockers.saving'));
    // Transient, like the two above: clears as soon as the locale lookup for
    // the listing's coordinates settles. Applies in edit mode too — an update
    // that moves a priced listing hits the same BE cap check as a publish.
    if (listingLocaleUnresolved) reasons.push(t('publishBlockers.checkingArea'));
    return reasons;
  }, [publishBlockers, aiGenerating, isSaving, listingLocaleUnresolved, t]);


  // The form holds a start plus a length; the wire format holds two absolute
  // datetimes. getEndDate/getEndTime bridge the two, and an overnight window
  // simply lands the end bound on the following date. The backend parses both
  // bounds as full naive datetimes, so it needs nothing else to handle one.
  // Shared with the publish reconciliation in handleSave so the windows a
  // publish falls back to are built exactly like the ones auto-save stages.
  function windowToWire(tw: TimeWindowState) {
    return {
      start: `${tw.date}T${tw.startTime}`,
      end: `${getEndDate(tw)}T${getEndTime(tw)}`,
      timezone: listingTimezone,
    };
  }

  // Both windows as the backend wants them, for a given method set.
  //
  // A method that isn't offered sends `{}`, not an omitted field. Omitted means
  // "leave it alone" to the backend, so only an explicit empty object clears a
  // window the listing already holds — which is what unchecking Drop-off has to
  // do, or the stale window keeps stretching the listing's envelope.
  //
  // `timeWindow` is deliberately absent: the server derives it from these two.
  function stageWindows(
    methods: string[],
    pickup: TimeWindowState,
    dropoff: TimeWindowState | null,
    radius: number = dropoffRadius,
    feeDollars: number = dropoffFeeDollars,
  ): PatchListingRequest {
    const hasDropoff = methods.includes('dropoff');
    return {
      fulfillmentMethod: methods,
      pickupWindow: methods.includes('pickup') ? windowToWire(pickup) : {},
      dropoffWindow: hasDropoff && dropoff ? windowToWire(dropoff) : {},
      // Omitted rather than nulled when drop-off isn't offered: an absent field
      // means "leave it alone" to the backend, and its reconcile step clears
      // the radius and fee itself once the method is gone. Sending null here
      // would read as "no change" and leave a stale delivery charge behind.
      ...(hasDropoff ? { dropoffRadius: radius, dropoffFee: feeDollars * 100 } : {}),
    };
  }

  // Commit windows into both React state and accumulatedFields so the values
  // are always in the publish payload — even if the chef never opens a picker.
  //
  // Seeding paths pass `dropoffSettings` explicitly: they set the radius/fee
  // state in the same render, so the closure here still holds the defaults
  // and staging from it would save 2 mi / Free over the chef's own values.
  function commitWindowsToAccumulated(
    pickup: TimeWindowState,
    dropoff: TimeWindowState | null = dropoffWindow,
    methods: string[] = fulfillment,
    dropoffSettings?: { radius: number; feeDollars: number },
  ) {
    setPickupWindow(pickup);
    setDropoffWindow(dropoff);
    accumulatedFields.current = {
      ...accumulatedFields.current,
      ...stageWindows(
        methods,
        pickup,
        dropoff,
        dropoffSettings?.radius ?? dropoffRadius,
        dropoffSettings?.feeDollars ?? dropoffFeeDollars,
      ),
    };
  }

  // Loads a saved listing's radius and fee into the wheels and returns them,
  // so the caller can stage the same values (see commitWindowsToAccumulated).
  function seedDropoffSettings(listing: { dropoffRadius?: number | null; dropoffFee?: number | null }) {
    const radius = snapDropoffRadius(listing.dropoffRadius);
    // Stored in cents; the form works in whole dollars.
    const feeDollars = listing.dropoffFee != null ? Math.round(listing.dropoffFee / 100) : 0;
    setDropoffRadius(radius);
    setDropoffFeeDollars(feeDollars);
    return { radius, feeDollars };
  }

  // Fresh defaults for a reset form (new listing, relist, copy). The drop-off
  // window only materializes when the method is actually offered.
  function defaultWindowsFor(methods: string[]): {
    pickup: TimeWindowState;
    dropoff: TimeWindowState | null;
  } {
    const pickup = buildDefaultWindow();
    return {
      pickup,
      dropoff: methods.includes('dropoff') ? buildDropoffWindowAfter(pickup) : null,
    };
  }

  // For new listings, seed default time window / price / quantity into
  // accumulatedFields on mount so the publish payload includes them even if
  // the user never touches those controls.
  const defaultSeeded = useRef(false);
  useEffect(() => {
    if (isEditMode || defaultSeeded.current) return;
    defaultSeeded.current = true;
    if (initialPending) {
      // Restored buffer already carries its price/quantity in
      // `accumulatedFields`; only re-commit the (clamped) windows so the
      // buffer's possibly-stale times don't get published as a past window.
      commitWindowsToAccumulated(pickupWindow, dropoffWindow, fulfillment);
      return;
    }
    commitWindowsToAccumulated(pickupWindow, dropoffWindow, fulfillment);
    accumulatedFields.current = {
      ...accumulatedFields.current,
      price: 0,
      quantity: 1,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep a NEW listing's stored timezone aligned with its location. The derived
  // zone flips from the browser fallback to the location's real zone once
  // coordinates resolve (profile auto-pick, device, or manual pick) — and that
  // can settle AFTER the default window is seeded (the auto-pick's onChange is a
  // child effect that runs before this component's seed effect). Re-stamp here
  // so the published timezone always reflects where the listing IS, not the
  // publisher's browser. Scoped to publish attempts (new / relist / draft) so
  // opening an existing published listing never silently rewrites its stored
  // timezone — those stay as-is unless the user explicitly changes the location.
  useEffect(() => {
    if (!isPublishingAttempt) return;
    // Both windows carry the zone, so both get re-stamped. Rewritten in place
    // off `accumulatedFields` rather than rebuilt from React state: the ref is
    // always current, where a state closure can be one render behind if a
    // window edit and a timezone resolution land in the same tick.
    const acc = accumulatedFields.current;
    const restamp = (w: unknown) => {
      const win = w as Record<string, unknown> | undefined;
      // An unoffered method stages as `{}`. Re-stamping it would turn "clear
      // this window" into a partial window the backend can't parse.
      return win && win.start ? { ...win, timezone: listingTimezone } : win;
    };
    const zoneOf = (w: unknown) => (w as { timezone?: string } | undefined)?.timezone;
    const stale =
      (!!(acc.pickupWindow as { start?: string } | undefined)?.start &&
        zoneOf(acc.pickupWindow) !== listingTimezone) ||
      (!!(acc.dropoffWindow as { start?: string } | undefined)?.start &&
        zoneOf(acc.dropoffWindow) !== listingTimezone);
    if (!stale) return;
    const restamped: PatchListingRequest = {
      pickupWindow: restamp(acc.pickupWindow),
      dropoffWindow: restamp(acc.dropoffWindow),
    };
    accumulatedFields.current = { ...accumulatedFields.current, ...restamped };
    if (listingId.current && !isPublishing) {
      savePatch(restamped);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listingTimezone, isPublishingAttempt]);

  // Mirror the in-progress dish to localStorage while logged out (new listing
  // only) so it survives a reload until the user creates an account at publish.
  // Every field handler updates a piece of React state AND accumulatedFields in
  // the same tick, so by the time this effect runs after a change, both are
  // current. A pending uploaded photo is in-memory only — never write its
  // blob-preview URL (persist null for the image when one is pending).
  useEffect(() => {
    if (isAuthenticated || editId) return;
    const hasPendingPhoto = pendingImageFileRef.current !== null;
    savePendingListing({
      title,
      description,
      spiciness,
      dietaryTags,
      originTags,
      allergenTags,
      price,
      quantity,
      fulfillment,
      shareLevel,
      timeWindow: pickupWindow,
      dropoffWindow,
      dropoffRadius,
      dropoffFeeDollars,
      location,
      imageUrl: hasPendingPhoto ? null : selectedImageUrl,
      thumbnailUrl: hasPendingPhoto ? null : selectedThumbnailUrl,
      accumulated: accumulatedFields.current,
    });
  }, [
    isAuthenticated, editId, title, description, spiciness, dietaryTags, originTags,
    allergenTags, price, quantity, fulfillment, shareLevel,
    pickupWindow, dropoffWindow, dropoffRadius, dropoffFeeDollars,
    location, selectedImageUrl, selectedThumbnailUrl,
  ]);

  // --- Pre-populate form when editing an existing listing ---
  const seeded = useRef(false);
  useEffect(() => {
    if (!existingListing || seeded.current) return;
    seeded.current = true;

    setTitle(existingListing.title ?? '');
    setDescription(existingListing.description ?? '');
    // The BE returns tags as `"value,label"` strings (see ApplyListingTranslation).
    // Strip to wire-format values for the form state — labels are looked up
    // dynamically via the tags registry at chip render time.
    setDietaryTags((existingListing.dietaryTags ?? []).map(tagValue));
    setOriginTags((existingListing.originTags ?? []).map(tagValue));
    setAllergenTags((existingListing.allergenTags ?? []).map(tagValue));
    setSpiciness(existingListing.spiciness ?? 0);
    // Listing prices are whole dollars (BE-enforced), so this is exact — no
    // rounding, which could silently change a stored price on re-save.
    setPrice(existingListing.price != null ? existingListing.price / 100 : 0);
    setQuantity(existingListing.quantity != null ? existingListing.quantity : 1);
    setFulfillment(existingListing.fulfillmentMethod ?? []);
    setSelectedImageUrl(existingListing.imageUrl ?? null);
    setSelectedThumbnailUrl(existingListing.thumbnailUrl ?? null);
    setShareLevel(
      existingListing.shareConnectionLevel ?? activeProfile?.shareConnectionLevel ?? 3,
    );

    if (isRelisting) {
      // Drop the source ID so ensureCreated POSTs a fresh listing on save,
      // and stage every copyable field into accumulatedFields so it gets
      // PATCHed onto the new listing once it exists. Title is sent via the
      // POST inside ensureCreated, so it isn't repeated here.
      listingId.current = null;
      accumulatedFields.current = {
        ...accumulatedFields.current,
        description: existingListing.description ?? undefined,
        // tagValue, not the raw arrays: the BE hands tags back as
        // `"value,label"` once translated, and persists whatever it's sent.
        // Copying the raw strings writes "vegan,Vegan" into the duplicate's
        // tag columns, which the feed's `dietary_tags @> '{vegan}'` filters
        // then miss entirely (both the include filter and the eater's
        // dietary-restriction exclusion).
        dietaryTags: existingListing.dietaryTags?.map(tagValue) ?? undefined,
        originTags: existingListing.originTags?.map(tagValue) ?? undefined,
        allergenTags: existingListing.allergenTags?.map(tagValue) ?? undefined,
        spiciness: existingListing.spiciness ?? undefined,
        price: existingListing.price ?? 0,
        quantity: existingListing.quantity ?? 1,
        fulfillmentMethod: existingListing.fulfillmentMethod ?? [],
        imageUrl: existingListing.imageUrl ?? undefined,
        thumbnailUrl: existingListing.thumbnailUrl ?? undefined,
        ingredients: existingListing.ingredients ?? undefined,
      };
      // Old windows have passed (or the listing was canceled / sold out) —
      // the user has to pick fresh times before publishing. Drop-off is
      // re-seeded off the new pickup default rather than carried over.
      {
        const relistMethods = existingListing.fulfillmentMethod ?? [];
        const fresh = defaultWindowsFor(relistMethods);
        commitWindowsToAccumulated(
          fresh.pickup,
          fresh.dropoff,
          relistMethods,
          seedDropoffSettings(existingListing),
        );
      }
      return;
    }

    // Seed price/quantity into accumulatedFields too — the picker wheels show
    // these as already-selected defaults, so publish validation must treat
    // them as set even if the user never touches them.
    accumulatedFields.current = {
      ...accumulatedFields.current,
      price: existingListing.price ?? 0,
      quantity: existingListing.quantity ?? 1,
    };

    // Published listings: the persisted window acts as a floor (originalWindow)
    // so the user can't republish back-in-time.
    // Drafts: restore the persisted window if its start is still in the future
    // (the user customized it and it's still valid). If start has passed, fall
    // back to the default — a past window is usually just the auto-created
    // one. Drafts never set originalWindow, so they have no publish floor.
    const isDraftListing = existingListing.status === 'draft';
    // Listings written before drop-off carry only `timeWindow`, and for those
    // it IS the pickup window (they were pickup-only). The backfill migration
    // fills `pickupWindow` in, but reading through the fallback means the form
    // works on either side of that migration.
    const pickupTw = existingListing.pickupWindow ?? existingListing.timeWindow;
    const dropoffTw = existingListing.dropoffWindow ?? null;
    const parsedPickup = fromTimeWindow(pickupTw);
    const parsedDropoff = fromTimeWindow(dropoffTw);

    // Record what the server currently holds so a failed save can revert the
    // controls back to it (see commitWindow). null until first persisted.
    lastSavedWindow.current = { pickup: parsedPickup, dropoff: parsedDropoff };

    const seedMethods = existingListing.fulfillmentMethod ?? [];
    const seededDropoff = seedDropoffSettings(existingListing);

    // Published listings: the persisted window acts as a floor (originalWindow)
    // so the user can't republish back-in-time. Drafts never set it, so they
    // have no publish floor.
    const pickFor = (
      parsed: TimeWindowState | null,
      raw: { start?: string } | null | undefined,
      kind: HandoffKind,
      fallback: TimeWindowState | null,
    ): TimeWindowState | null => {
      if (parsed && !isDraftListing) {
        originalWindow.current[kind] = parsed;
        return buildDefaultWindow(parsed);
      }
      // Draft: keep the persisted window if its start is still ahead. A past
      // start is usually just the auto-created default, so fall back.
      if (parsed && raw?.start && new Date(raw.start).getTime() > Date.now()) {
        return parsed;
      }
      return fallback;
    };

    const nextPickup =
      pickFor(parsedPickup, pickupTw, 'pickup', buildDefaultWindow()) ?? buildDefaultWindow();
    const nextDropoff = seedMethods.includes('dropoff')
      ? pickFor(parsedDropoff, dropoffTw, 'dropoff', buildDropoffWindowAfter(nextPickup))
      : null;

    commitWindowsToAccumulated(nextPickup, nextDropoff, seedMethods, seededDropoff);
  }, [existingListing, isRelisting]);

  // The LocationPicker auto-picks a location from the profile on mount when
  // its `value` is null — for a new listing that's correct, but when editing
  // an existing listing it would re-PATCH the location every time the page
  // opens. We delay mounting the picker until we've decided what value to
  // seed it with: nothing for a brand-new listing, or the listing's own
  // saved `listingLocation` (owner-only field, included in the API response
  // when the caller owns the listing). Falls back to the profile address if
  // somehow the listing has obfuscated coords but no saved address yet.
  //
  // Relist is treated like a new listing here: we leave `location` null so
  // the picker auto-picks from profile, which fires onChange → savePatch and
  // gets the location buffered for the soon-to-be-created listing.
  const [locationReady, setLocationReady] = useState(!editId);
  useEffect(() => {
    if (locationReady) return;
    if (!editId || isRelisting) {
      setLocationReady(true);
      return;
    }
    if (!existingListing) return;
    if (existingListing.obfuscatedLocation && !activeProfile) return;

    const seededListing = buildListingLocation(existingListing.listingLocation);
    if (seededListing) {
      setLocation(seededListing);
      setLocationReady(true);
      return;
    }

    if (existingListing.obfuscatedLocation && activeProfile) {
      const profileAddress =
        activeProfile.metadata?.addresses?.find((a) => a.type === 'profile') ?? null;
      const seeded = buildProfileLocation(profileAddress);
      if (seeded) setLocation(seeded);
    }
    setLocationReady(true);
  }, [editId, isRelisting, existingListing, activeProfile, locationReady]);

  // --- Helpers ---

  /** Create the draft listing on first meaningful interaction (title blur). */
  async function ensureCreated(): Promise<string | null> {
    if (listingId.current) return listingId.current;
    if (!title.trim() || !activeProfile) return null;

    try {
      const res = await createMutation.mutateAsync({
        type: 'dish',
        title: title.trim(),
        listerId: activeProfile.id,
        fulfillmentMethod: fulfillment,
      });
      listingId.current = res.id;
      // The backend stamps a default placeholder image on a brand-new draft.
      // Only adopt it if the user hasn't already chosen their own — otherwise
      // we'd clobber their pick with Generic_Box.png. Their choice was buffered
      // into accumulatedFields and is flushed to the server just below.
      if (!userChoseImageRef.current) {
        if (res.imageUrl) setSelectedImageUrl(res.imageUrl);
        if (res.thumbnailUrl) setSelectedThumbnailUrl(res.thumbnailUrl);
      }
      // Flush any patches that were buffered before the listing existed.
      if (Object.keys(accumulatedFields.current).length > 0) {
        patchQueue.enqueue(res.id, accumulatedFields.current).then(
          (r) => logDebug('[PublishListing] flushed buffered fields', undefined, { result: r }),
          (e) => console.error('[PublishListing] flush failed', e),
        );
      }
      logDebug('[PublishListing] created draft', undefined, { result: res });
      return res.id;
    } catch (e) {
      console.error('[PublishListing] create failed', e);
      return null;
    }
  }

  // Accumulate every patch so handleSave can replay the full state without
  // manually listing every field. Each savePatch merges into this ref. Seeds
  // from a buffered logged-out dish so the create-flush after signup carries
  // everything the user already entered.
  const accumulatedFields = useRef<PatchListingRequest>(initialPending?.accumulated ?? {});

  // Logged-out image upload: a real upload needs a listing id + auth, so when
  // logged out we hold the chosen File in memory and preview it via an object
  // URL. The upload happens in the publish flush, right after the listing is
  // created post-signup. Not persisted to localStorage (Files don't serialize),
  // so a cold reload drops it — the rest of the dish still restores.
  const pendingImageFileRef = useRef<File | null>(null);
  const pendingPreviewUrlRef = useRef<string | null>(null);
  useEffect(() => {
    // Revoke the last object URL on unmount to avoid a leak.
    return () => {
      if (pendingPreviewUrlRef.current) URL.revokeObjectURL(pendingPreviewUrlRef.current);
    };
  }, []);

  // Once Publish is clicked we stop accepting new auto-save patches — the
  // publish patch already carries every buffered field and any further
  // background PATCH would race the navigation.
  const [isPublishing, setIsPublishing] = useState(false);
  // First-publish visibility prompt state.
  const [visibilityModalOpen, setVisibilityModalOpen] = useState(false);
  const visibilityHandledRef = useRef(false);
  const updateReach = useUpdateShareConnectionLevel();
  // Publish-time profile-address prompt state. A profile address is required for
  // Stripe tax (ship-from/billing jurisdiction); rather than block the form we
  // intercept the publish for chefs who lack one. The ref stashes the resolved
  // share level so the resumed publish still stamps the right reach.
  const [addressModalOpen, setAddressModalOpen] = useState(false);
  const addressHandledRef = useRef(false);
  const pendingShareLevelRef = useRef<number | undefined>(undefined);
  // Publish-time chef-quiz gate. Eaters can fill out the whole form, but on the
  // final publish they must pass the chef food-safety quiz (becoming a chef)
  // before the listing goes live. Chefs skip it. The ref bypasses the prompt on
  // the post-quiz re-entry into handleSave.
  const [chefQuizModalOpen, setChefQuizModalOpen] = useState(false);
  const chefQuizHandledRef = useRef(false);
  // Logged-out publish gate. A logged-out user can build the whole dish; on the
  // final publish we prompt account creation. Once authenticated, the
  // materialize effect below turns the buffered dish into a real draft.
  const [signupGateOpen, setSignupGateOpen] = useState(false);
  // Has the chef ever published before? First publish → modal (sets default);
  // afterwards → inline per-dish override in the form. `undefined` myListings
  // means still loading (treat as "can't tell yet").
  const listingsLoaded = myListings !== undefined;
  const hasPublishedBefore = (myListings ?? []).some((l) => l.status !== 'draft');

  // "How to use HomePlated" popup for anyone who has never published, shown on
  // entering the publish flow. Logged-out visitors count as never-published.
  // Keeps showing on each visit until their first publish; the ref stops it
  // re-opening within a visit (e.g. when signing up at the publish gate flips
  // isAuthenticated and myListings reloads).
  const [showInstructions, setShowInstructions] = useState(false);
  const instructionsShownRef = useRef(false);
  useEffect(() => {
    if (instructionsShownRef.current) return;
    const neverPublished = !isAuthenticated || (listingsLoaded && !hasPublishedBefore);
    if (!neverPublished) return;
    instructionsShownRef.current = true;
    setShowInstructions(true);
  }, [isAuthenticated, listingsLoaded, hasPublishedBefore]);

  /** Patch the listing. Always accumulates fields; only fires the API call
   *  if the listing has been created. Pre-creation patches are buffered and
   *  flushed automatically by ensureCreated. */
  async function savePatch(fields: PatchListingRequest) {
    accumulatedFields.current = { ...accumulatedFields.current, ...fields };

    const id = listingId.current;
    if (!id) return;
    if (isPublishing) return;

    try {
      const res = await patchQueue.enqueue(id, fields);
      logDebug('[PublishListing] patched', undefined, { fields, result: res });
    } catch (e) {
      console.error('[PublishListing] patch failed', e);
    }
  }

  // --- Field handlers ---

  function startAi(...fields: AiField[]) {
    setAiGenerating((prev) => { const next = new Set(prev); fields.forEach((f) => next.add(f)); return next; });
  }
  function stopAi(...fields: AiField[]) {
    setAiGenerating((prev) => { const next = new Set(prev); fields.forEach((f) => next.delete(f)); return next; });
  }

  function cancelAiGeneration() {
    aiAbortRef.current?.abort();
    aiAbortRef.current = null;
    setAiGenerating(new Set());
  }

  /** Opt out of the AI-picked image only — the user wants to choose their own.
   *  We don't abort the in-flight request (ingredients/tags come back in the
   *  same call and should still fill); we just latch the "user owns the image"
   *  flag so the returned image is ignored by the apply-guard, and clear the
   *  image spinner so the upload/preset buttons appear right away. */
  function dismissAiImage() {
    userChoseImageRef.current = true;
    stopAi('image');
  }

  async function triggerGenerateFrom(id: string | null, from: 'description' | 'ingredients') {
    // Logged-out callers have no backend listing — generate statelessly from
    // the title/description via the service key, applying results to the
    // buffered form. Authenticated callers regenerate against their draft (id).
    const loggedOut = !activeProfile;
    const gen = async (fields: string[]) => {
      const controller = new AbortController();
      aiAbortRef.current = controller;
      const timeoutId = setTimeout(() => controller.abort(), AI_GENERATION_TIMEOUT_MS);
      try {
        return await generateMutation.mutateAsync(
          loggedOut
            ? {
                title: title.trim(),
                description: description.trim() || undefined,
                fields,
                signal: controller.signal,
                serviceKey: GENERATE_SERVICE_KEY,
              }
            : {
                listingId: id!,
                fields,
                signal: controller.signal,
              },
        );
      } finally {
        clearTimeout(timeoutId);
        if (aiAbortRef.current === controller) aiAbortRef.current = null;
      }
    };

    // If the user already picked an image, the AI must not touch it — don't
    // request it, don't show a spinner over their picture, don't apply a result.
    const wantImage = !userChoseImageRef.current;

    // Mark every field that's going to be filled as generating so the UI shows
    // overlays everywhere up front. Description clears as soon as call 1 returns;
    // ingredients/tags/image clear together when call 2 returns.
    const initialFields: AiField[] = [
      ...(from === 'description' ? (['description'] as AiField[]) : []),
      'ingredients',
      'tags',
      ...(wantImage ? (['image'] as AiField[]) : []),
    ];
    startAi(...initialFields);

    try {
      if (from === 'description') {
        const descResult = await gen(['description']);
        if (descResult.description) {
          setDescription(descResult.description);
          // Authed gen persisted these on the listing server-side; logged-out
          // gen persisted nothing, so buffer the results (savePatch no-ops the
          // API when there's no listing yet) or they'd be lost on publish.
          if (loggedOut) savePatch({ description: descResult.description });
        }
        stopAi('description');
      }

      const batchResult = await gen([
        'ingredientsItems',
        'tags',
        ...(wantImage ? ['image'] : []),
      ]);

      if (loggedOut && batchResult.ingredientsItems?.length) {
        savePatch({ ingredients: { ingredients: batchResult.ingredientsItems } });
      }
      if (batchResult.tags) {
        const t = batchResult.tags;
        if (t.dietary) setDietaryTags(t.dietary);
        if (t.allergens) setAllergenTags(t.allergens);
        if (t.origins) setOriginTags(t.origins);
        if (t.spicy != null) setSpiciness(t.spicy);
        if (loggedOut) {
          const tagPatch: PatchListingRequest = {};
          if (t.dietary) tagPatch.dietaryTags = t.dietary;
          if (t.allergens) tagPatch.allergenTags = t.allergens;
          if (t.origins) tagPatch.originTags = t.origins;
          if (t.spicy != null) tagPatch.spiciness = t.spicy;
          savePatch(tagPatch);
        }
      }
      // Re-check the ref, not just wantImage: the user may have picked an image
      // while this batch call was in flight. Their choice always wins.
      if (batchResult.image && !userChoseImageRef.current) {
        setSelectedImageUrl(batchResult.image.public_url);
        setSelectedThumbnailUrl(batchResult.image.public_url);
        if (loggedOut) {
          savePatch({
            imageUrl: batchResult.image.public_url,
            thumbnailUrl: batchResult.image.public_url,
          });
        }
      }
      stopAi('ingredients', 'tags', 'image');
    } catch (e) {
      const isAbort = e instanceof DOMException && e.name === 'AbortError';
      if (!isAbort) console.error('[PublishListing] AI generation failed', e);
      setAiGenerating(new Set());
    }
  }

  /** Logged-out AI gen is allowed once the user has set a location and we have
   *  a service key to call with — and never in edit/relist mode (pre-filled). */
  const canGenerateLoggedOut =
    !activeProfile && !!GENERATE_SERVICE_KEY && !editId && isSufficientLocation(userLocation);

  async function handleTitleBlur() {
    if (!title.trim()) return;
    if (!activeProfile) {
      // Logged out: no draft to create. Run stateless generation from the title.
      if (canGenerateLoggedOut) triggerGenerateFrom(null, 'description');
      return;
    }
    if (!listingId.current) {
      const id = await ensureCreated();
      // Skip AI regen when the form was pre-filled (edit or relist) — there's
      // already user-curated content we'd be clobbering.
      if (id && !editId) triggerGenerateFrom(id, 'description');
    } else {
      await savePatch({ title: title.trim() });
      triggerGenerateFrom(listingId.current, 'description');
    }
  }

  async function handleDescriptionBlur() {
    if (!description.trim()) return;
    if (!activeProfile) {
      // Logged out: regenerate ingredients/tags/image from the description
      // (needs a title to anchor the prompt).
      if (canGenerateLoggedOut && title.trim()) triggerGenerateFrom(null, 'ingredients');
      return;
    }
    await savePatch({ description: description.trim() });
    if (listingId.current) {
      triggerGenerateFrom(listingId.current, 'ingredients');
    }
  }

  function toggleTag(tag: string, current: string[], setter: (v: string[]) => void, field: keyof PatchListingRequest) {
    const next = current.includes(tag) ? current.filter((t) => t !== tag) : [...current, tag];
    setter(next);
    savePatch({ [field]: next });
  }

  const handlePriceChange = useCallback((val: number | string) => {
    if (val === 'blue_star_chef') {
      setPrice(wheelTopDollars);
      savePatch({ price: wheelTopDollars * 100 });
      setShowBlueStarSheet(true);
      return;
    }
    const n = Number(val);
    // Over-cap grayed price: the wheel bounces back to the cap, so pin the
    // price to the cap and surface the "not available in your area" explainer
    // instead of persisting an out-of-cap value.
    if (n > wheelTopDollars) {
      setPrice(wheelTopDollars);
      savePatch({ price: wheelTopDollars * 100 });
      setShowLockedPriceSheet(true);
      return;
    }
    setPrice(n);
    savePatch({ price: n * 100 });
    // First-ever lister picking a paid price → gentle nudge toward free dishes.
    // `listingsLoaded` guards against an established chef getting flashed the
    // sheet while their listings are still loading (undefined → not-published).
    if (n > 0 && listingsLoaded && !hasPublishedBefore && !firstPriceNudgeShownRef.current) {
      firstPriceNudgeShownRef.current = true;
      setShowFirstPriceNudge(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wheelTopDollars, listingsLoaded, hasPublishedBefore]);

  const handleQuantityChange = useCallback((val: number | string) => {
    if (val === 'feed_community') {
      setQuantity(10);
      savePatch({ quantity: 10 });
      setShowFeedCommunitySheet(true);
      return;
    }
    const n = Number(val);
    setQuantity(n);
    savePatch({ quantity: n });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The hand-off cards to render, earliest window first.
  const handoffCards = useMemo(() => {
    const pickupShown = offersPickup ? pickupWindow : null;
    const dropoffShown = offersDropoff ? dropoffWindow : null;
    const cards: {
      kind: HandoffKind;
      window: TimeWindowState;
      bounds: DurationBounds;
      /** The sibling window, so each sheet can refuse a Done that overlaps it. */
      other: TimeWindowState | null;
    }[] = [];
    if (pickupShown) {
      cards.push({
        kind: 'pickup',
        window: pickupShown,
        bounds: PICKUP_DURATION_BOUNDS,
        other: dropoffShown,
      });
    }
    if (dropoffShown) {
      cards.push({
        kind: 'dropoff',
        window: dropoffShown,
        bounds: DROPOFF_DURATION_BOUNDS,
        other: pickupShown,
      });
    }
    return cards.sort((a, b) => compareWindowStart(a.window, b.window));
  }, [offersPickup, offersDropoff, pickupWindow, dropoffWindow]);

  // --- Hand-off window handlers ---
  // All constraint logic lives in utils/listingTimeWindow; HandoffWindowCard
  // clamps each edit against its own bounds before it reaches us, so the
  // handlers below only commit and persist.

  // Apply a window edit optimistically, persist it, then reconcile from the
  // server response so the UI and backend can never diverge:
  //   - success → adopt the canonical window the server returns (in case it
  //     normalized/clamped anything)
  //   - failure → revert that window to the last confirmed value + warn
  // `windowSaveSeq` is per-kind, so a drop-off save landing late can't revert
  // or supersede an unrelated pickup edit.
  function commitWindow(kind: HandoffKind, next: TimeWindowState) {
    const pickup = kind === 'pickup' ? next : pickupWindow;
    const dropoff = kind === 'dropoff' ? next : dropoffWindow;
    commitWindowsToAccumulated(pickup, dropoff);

    const id = listingId.current;
    if (!id || isPublishing) return;

    const fields: PatchListingRequest =
      kind === 'pickup'
        ? { pickupWindow: accumulatedFields.current.pickupWindow }
        : { dropoffWindow: accumulatedFields.current.dropoffWindow };

    const seq = ++windowSaveSeq.current[kind];
    patchQueue.enqueue(id, fields).then(
      (res) => {
        if (seq !== windowSaveSeq.current[kind]) return; // superseded by a newer edit
        const confirmed = fromTimeWindow(
          kind === 'pickup' ? res.pickupWindow : res.dropoffWindow,
        );
        if (!confirmed) return;
        lastSavedWindow.current[kind] = confirmed;
        if (kind === 'pickup') setPickupWindow(confirmed);
        else setDropoffWindow(confirmed);
        // Adopt the server's object verbatim rather than re-deriving it —
        // windowToWire would re-stamp the timezone from this render's memo,
        // which can differ from what the server actually stored.
        accumulatedFields.current = {
          ...accumulatedFields.current,
          [kind === 'pickup' ? 'pickupWindow' : 'dropoffWindow']: (kind === 'pickup'
            ? res.pickupWindow
            : res.dropoffWindow) as Record<string, unknown>,
        };
      },
      (e) => {
        console.error('[PublishListing] window save failed', kind, e);
        if (seq !== windowSaveSeq.current[kind]) return; // a newer edit will settle
        const last = lastSavedWindow.current[kind];
        if (last) {
          if (kind === 'pickup') commitWindowsToAccumulated(last, dropoffWindow);
          else commitWindowsToAccumulated(pickupWindow, last);
        }
        notify.error(t('errors.windowSaveFailed'));
      },
    );
  }

  function handleDropoffRadiusChange(miles: number) {
    setDropoffRadius(miles);
    savePatch(stageWindows(fulfillment, pickupWindow, dropoffWindow, miles, dropoffFeeDollars));
  }

  function handleDropoffFeeChange(dollars: number) {
    setDropoffFeeDollars(dollars);
    savePatch(stageWindows(fulfillment, pickupWindow, dropoffWindow, dropoffRadius, dollars));
  }

  // Check/uncheck a hand-off method. Sends the method set and both windows in
  // one patch so the server never sees a moment where drop-off is offered with
  // no window (or a window lingers for a method that was just dropped).
  function handleToggleHandoff(kind: HandoffKind) {
    // The overlay above blocks the tap; this keeps any other caller honest.
    if (handoffMethodsLocked) return;
    const next = fulfillment.includes(kind)
      ? fulfillment.filter((m) => m !== kind)
      : [...fulfillment, kind];
    // Clearing both is allowed: it's the state a new listing starts in, so
    // refusing to return to it would be arbitrary. Publish stays blocked until
    // one is picked (publishBlockers.pickHandoff).
    setFulfillment(next);

    let pickup = pickupWindow;
    let dropoff = dropoffWindow;
    const checking = !fulfillment.includes(kind);

    // Seed a drop-off window the first time it's checked.
    if (checking && kind === 'dropoff' && !dropoff) {
      dropoff = buildDefaultWindow(undefined, DROPOFF_DURATION_BOUNDS);
    }

    // Whichever method was just checked SECOND gets slid clear of the one
    // already selected, so the chef never lands on an overlap they then have to
    // untangle. Both windows default to the same start time, so checking them
    // in either order collided: drop-off first put a 30-minute run at 6:30, and
    // checking pick-up next dropped a 2-hour window on top of it.
    //
    // Only the window being turned ON moves. Editing a window by hand is left
    // alone — those times were chosen deliberately, so an overlap there is
    // reported by publishBlockers rather than silently rewritten.
    if (checking && next.includes('pickup') && next.includes('dropoff') && dropoff) {
      if (kind === 'dropoff' && windowsOverlap(pickup, dropoff)) {
        dropoff = clampWindow(
          windowStartingAfter(dropoff, pickup),
          originalWindow.current.dropoff,
          DROPOFF_DURATION_BOUNDS,
        );
      } else if (kind === 'pickup' && windowsOverlap(pickup, dropoff)) {
        pickup = clampWindow(
          windowStartingAfter(pickup, dropoff),
          originalWindow.current.pickup,
          PICKUP_DURATION_BOUNDS,
        );
      }
    }

    // Unchecking keeps `dropoff` in local state on purpose — stageWindows sends
    // `{}` because the method is gone, so the server clears it, but re-checking
    // restores the times the chef already picked instead of a fresh default.
    commitWindowsToAccumulated(pickup, dropoff, next);
    savePatch(stageWindows(next, pickup, dropoff));
  }

  async function handleImageUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    // Reset early so the same file can be re-selected even on the buffered path.
    e.target.value = '';

    // Logged out: no listing/auth to upload against yet. Hold the File and
    // preview it locally; the publish flush uploads it once the account exists.
    if (!activeProfile) {
      pendingImageFileRef.current = file;
      if (pendingPreviewUrlRef.current) URL.revokeObjectURL(pendingPreviewUrlRef.current);
      const preview = URL.createObjectURL(file);
      pendingPreviewUrlRef.current = preview;
      userChoseImageRef.current = true;
      setSelectedImageUrl(preview);
      setSelectedThumbnailUrl(preview);
      return;
    }

    const id = listingId.current ?? (await ensureCreated());
    if (!id) return;

    uploadImageMutation.mutate(
      { listingId: id, file },
      {
        onSuccess: (res) => {
          userChoseImageRef.current = true;
          setSelectedImageUrl(res.imageUrl);
          setSelectedThumbnailUrl(res.thumbnailUrl);
          // Keep the patch buffer in sync — in relist mode the buffer was
          // seeded with the source listing's old imageUrl, and a later
          // savePatch would otherwise replay it and undo this upload.
          accumulatedFields.current = {
            ...accumulatedFields.current,
            imageUrl: res.imageUrl ?? undefined,
            thumbnailUrl: res.thumbnailUrl ?? undefined,
          };
          logDebug('[PublishListing] image uploaded', undefined, { result: res });
        },
        onError: (err) => {
          console.error('[PublishListing] image upload failed', err);
          notify.error(t('errors.imageUploadFailed'));
        },
      },
    );
  }

  /**
   * Persist the chosen default visibility, then continue the publish. Called
   * from the first-publish prompt; the ref bypasses the prompt on the re-entry.
   */
  async function handleVisibilityConfirm(level: number) {
    if (activeProfile) {
      try {
        await updateReach.mutateAsync({
          profileId: activeProfile.id,
          shareConnectionLevel: level,
        });
      } catch {
        // Best-effort: a failed default-save shouldn't block publishing — the
        // listing just inherits the unchanged profile default.
      }
    }
    setShareLevel(level);
    visibilityHandledRef.current = true;
    setVisibilityModalOpen(false);
    // Pass the chosen level explicitly: activeProfile in this closure is still
    // the pre-update value until the next render, so we can't rely on it here.
    void handleSave({ publish: true, shareLevelOverride: level });
  }

  /**
   * The user dismissed the profile-address gate without saving one (the X /
   * Cancel on the dish-location choice screen — the picker screen steps back to
   * that choice instead of closing). The publish never happened, so say so:
   * `handleSave` returned early before any status change, and with the modal
   * simply vanishing there is otherwise no signal that the dish is still a
   * draft. Also release the first-publish visibility prompt so the next Publish
   * walks the whole first-publish flow again rather than jumping straight back
   * to this gate.
   */
  function handleAddressGateDismissed() {
    setAddressModalOpen(false);
    visibilityHandledRef.current = false;
    pendingShareLevelRef.current = undefined;
    notify.info(t('location.gateCancelled'));
  }

  // The profile-address modal confirmed (and the backend confirmed) a saved
  // profile address. Mark the gate handled and resume the publish, preserving
  // any share level resolved by the visibility prompt.
  function handleAddressSaved() {
    addressHandledRef.current = true;
    setAddressModalOpen(false);
    void handleSave({ publish: true, shareLevelOverride: pendingShareLevelRef.current });
  }

  // The eater passed the chef quiz and is now a chef. Mark the gate handled and
  // resume the publish from the top (the visibility/address gates then run as
  // they would for any first-time publisher).
  function handleBecameChef() {
    chefQuizHandledRef.current = true;
    setChefQuizModalOpen(false);
    void handleSave({ publish: true });
  }

  // Once a logged-out, buffered dish has an account behind it, graduate the
  // localStorage buffer into a real backend draft: create the listing, upload
  // any in-memory photo, clear the buffer, and navigate to the real draft URL.
  // From there it's an ordinary authed draft — durable across refresh /
  // navigation / devices and visible in the user's drafts; the user proceeds to
  // Publish (which runs the chef-quiz gate for the new eater). Fires when auth
  // flips (in-page signup) AND on a fresh authed mount that finds a buffer
  // (refresh / navigate-back — the case that otherwise lands on an empty page).
  // Editing an existing listing (editId) is exempt.
  const materializedRef = useRef(false);
  useEffect(() => {
    if (materializedRef.current || editId) return;
    if (!isAuthenticated || !activeProfile) return;
    const buffer = loadPendingListing();
    if (!buffer || !buffer.title.trim()) {
      // No meaningful buffered dish — drop any junk buffer so it can't linger.
      if (buffer) clearPendingListing();
      return;
    }
    materializedRef.current = true;
    setSignupGateOpen(false);
    void materializePendingListing(buffer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, activeProfile, editId]);

  async function materializePendingListing(buffer: PendingListingSnapshot) {
    if (!activeProfile) return;
    try {
      const created = await createMutation.mutateAsync({
        type: 'dish',
        title: buffer.title.trim(),
        listerId: activeProfile.id,
        fulfillmentMethod: buffer.fulfillment ?? [],
      });

      const fields: PatchListingRequest = { ...buffer.accumulated };
      // A photo uploaded this session is held in memory only; upload it now if
      // it survived (in-page signup). After a cold reload the File is gone and
      // `fields.imageUrl` carries any library/AI image the buffer persisted.
      if (pendingImageFileRef.current) {
        try {
          const up = await uploadImageMutation.mutateAsync({
            listingId: created.id,
            file: pendingImageFileRef.current,
          });
          fields.imageUrl = up.imageUrl ?? fields.imageUrl;
          fields.thumbnailUrl = up.thumbnailUrl ?? fields.thumbnailUrl;
        } catch (e) {
          console.error('[PublishListing] materialize image upload failed', e);
        }
        pendingImageFileRef.current = null;
      }

      if (Object.keys(fields).length > 0) {
        await patchQueue.enqueue(created.id, fields);
      }

      clearPendingListing();
      history.replace(`/publish-listing/${created.id}`);
    } catch (e) {
      console.error('[PublishListing] materialize failed', e);
      materializedRef.current = false; // allow a retry
    }
  }

  async function handleSave({
    publish,
    shareLevelOverride,
  }: {
    publish: boolean;
    shareLevelOverride?: number;
  }) {
    // Logged-out gate: build the whole dish, then create the account at the
    // final publish. Runs first. Once the user authenticates, the materialize
    // effect turns the buffered dish into a real draft and navigates to it; the
    // user then taps Publish there, which hits the chef-quiz gate below.
    if (publish && !isAuthenticated) {
      setSignupGateOpen(true);
      return;
    }

    // Chef-quiz gate: an authenticated eater must become a chef before the
    // listing can go live. Runs before visibility/address so a brand-new chef
    // resolves their role, then their first-publish prompts. The ref
    // short-circuits on the post-quiz re-entry (activeProfile in this closure
    // may still read as an eater until the next render). Chefs skip this.
    if (
      publish &&
      !chefQuizHandledRef.current &&
      activeProfile &&
      !isChef(activeProfile)
    ) {
      setChefQuizModalOpen(true);
      return;
    }

    // First time a chef publishes (no prior non-draft listing), ask for their
    // default audience and persist it before completing the publish. Returning
    // chefs set per-dish reach inline in the form instead.
    if (
      publish &&
      !visibilityHandledRef.current &&
      activeProfile &&
      listingsLoaded &&
      !hasPublishedBefore
    ) {
      setVisibilityModalOpen(true);
      return;
    }

    // Every lister must have a real, FULL profile address before publishing.
    // Don't block the form, but intercept the publish to prompt for one —
    // offering the dish's own location as a one-tap option. Runs after the
    // visibility gate so a first-time publisher resolves reach first.
    //
    // We require a type:'profile' address that is complete (street1 or a
    // confirmed pin, plus city/state/postal/country — see isFullProfileAddress).
    // Anything less triggers the prompt: a ZIP-only lister, an orderer's
    // minimum postal+country address, the top-level postal/country scalars the
    // location gate stamps, or a partial/legacy profile address.
    const profileAddr = activeProfile?.metadata?.addresses?.find((a) => a.type === 'profile');
    const hasFullAddress = isFullProfileAddress(profileAddr);
    if (publish && !addressHandledRef.current && activeProfile && !hasFullAddress) {
      pendingShareLevelRef.current = shareLevelOverride;
      setAddressModalOpen(true);
      return;
    }

    let id = listingId.current;

    // No listing exists yet. Two cases land here:
    //   • Brand-new listing where the user hasn't even blurred the title —
    //     nothing meaningful to persist, just leave.
    //   • Relist: the form is pre-filled from the source, but the new
    //     listing hasn't been POSTed. Create it now and let ensureCreated
    //     flush the buffered fields.
    if (!id) {
      id = await ensureCreated();
      if (!id) {
        if (!publish) history.replace('/my-kitchen');
        return;
      }
    }

    // Flush a photo the user uploaded while logged out (held in memory). Now
    // that the listing exists we can upload it; merge the resulting URLs into
    // the buffer so the publish PATCH below carries them. Runs once — the File
    // is cleared after. Non-fatal: a failed upload still lets the dish publish.
    if (pendingImageFileRef.current) {
      const file = pendingImageFileRef.current;
      pendingImageFileRef.current = null;
      try {
        const uploaded = await uploadImageMutation.mutateAsync({ listingId: id, file });
        accumulatedFields.current = {
          ...accumulatedFields.current,
          imageUrl: uploaded.imageUrl ?? undefined,
          thumbnailUrl: uploaded.thumbnailUrl ?? undefined,
        };
      } catch (err) {
        console.error('[PublishListing] pending image upload failed', err);
      }
      if (pendingPreviewUrlRef.current) {
        URL.revokeObjectURL(pendingPreviewUrlRef.current);
        pendingPreviewUrlRef.current = null;
      }
    }

    // Lock auto-save so a debounced field PATCH can't land after the publish
    // PATCH (which would overwrite status). Stays locked through navigation;
    // re-enabled in the catch block if we stay on this page.
    if (publish) setIsPublishing(true);

    // Wait for every queued auto-save to settle so their responses (e.g. the
    // server's canonical windows) are merged into accumulatedFields before we
    // replay them in the publish PATCH. drain() never rejects, so a previously
    // failed auto-save doesn't block the user from retrying via publish.
    await patchQueue.drain();

    // Replay all accumulated patches plus (optionally) the status transition.
    // Every savePatch call has already merged its fields into
    // accumulatedFields, so we don't need to manually enumerate form fields.
    const fields: PatchListingRequest = { ...accumulatedFields.current };

    if (publish) {
      fields.status = 'scheduled';
      // Stamp the listing's audience reach at publish time. The draft was
      // stamped from the profile default at create time, which is stale if the
      // chef just set/changed their default (e.g. the first-publish prompt) —
      // so re-apply the intended level here. (Future per-dish override feeds in
      // through the same field via shareLevelOverride.)
      fields.shareConnectionLevel = shareLevelOverride ?? shareLevel;

      // Reconcile the fields publish validation actually checked. This form has
      // two sources of truth: `publishBlockers` reads React state, while the
      // payload above is replayed from `accumulatedFields`, and nothing kept
      // them in step. Any value that reached state without its handler also
      // mirroring it into the accumulator — a seeded default, a programmatic
      // setLocation — cleared validation and then published as ABSENT.
      // Publishing without a location is the damaging case: the feed's locale
      // filter builds a point from the coords, so NULL coords make ST_DWithin
      // return NULL rather than false and the listing is dropped by every feed
      // query, permanently, while still showing in its own lister's panel as
      // though it were live.
      //
      // Fill only what is missing, never overwrite: a field that went through a
      // handler already holds the value that handler intended (a re-stamped
      // window timezone, say), and state is only the fallback for the fields
      // that never made the trip.
      if (!fields.title && title.trim()) fields.title = title.trim();
      if (fields.price == null) fields.price = price * 100;
      if (fields.quantity == null) fields.quantity = quantity;
      // Hand-off fields as auto-save would stage them. `undefined` is the only
      // "missing" here: a staged `{}` is a deliberate clear of an unoffered
      // method's window and must survive.
      const staged = stageWindows(fulfillment, pickupWindow, dropoffWindow);
      if (!fields.fulfillmentMethod) fields.fulfillmentMethod = staged.fulfillmentMethod;
      if (fields.pickupWindow === undefined) fields.pickupWindow = staged.pickupWindow;
      if (fields.dropoffWindow === undefined) fields.dropoffWindow = staged.dropoffWindow;
      if (fields.dropoffRadius == null && staged.dropoffRadius != null) {
        fields.dropoffRadius = staged.dropoffRadius;
      }
      if (fields.dropoffFee == null && staged.dropoffFee != null) {
        fields.dropoffFee = staged.dropoffFee;
      }
      if (!fields.listingLocation && location) {
        const addr = location.address;
        fields.listingLocation = {
          lat: addr.lat,
          lon: addr.lon,
          street1: addr.street1,
          street2: addr.street2,
          city: addr.city,
          stateProvince: addr.stateProvince,
          postalCode: addr.postalCode,
          country: addr.country,
          placeId: addr.placeId,
          noUnit: addr.noUnit,
          pinConfirmed: addr.pinConfirmed,
        };
        fields.postalCode = fields.postalCode ?? addr.postalCode;
        fields.stateProvince = fields.stateProvince ?? addr.stateProvince;
      }
    }

    try {
      // Through the queue too, so a save that slips in after the drain still
      // goes out after this one instead of racing it.
      const res = await patchQueue.enqueue(id, fields);
      logDebug('[PublishListing] saved', undefined, { result: res });

      // Guard: if we asked the server to publish but it didn't honor the
      // status transition, treat that as a failure rather than navigating
      // the user to a "published" listing that's still actually a draft.
      if (publish && res.status !== 'scheduled' && res.status !== 'active') {
        throw new Error(`unexpected post-publish status: ${res.status}`);
      }

      if (publish) {
        maybeWriteBackUnitToProfile();
        // The dish is live — drop any logged-out buffer so a later visit starts
        // fresh instead of restoring an already-published dish.
        clearPendingListing();
        // Flag the just-published id so the listing detail page can fire
        // confetti once the route transition completes. Tied to the id so
        // we can't accidentally celebrate on the wrong listing.
        markJustPublished(id);
      }

      // Saving a draft as a draft (new listing or editing a draft) →
      // My Kitchen's Drafts tab. Anything else (publish, or update of an
      // already-published listing) → the listing detail.
      // `?tab=drafts` is explicit rather than left to My Kitchen's count-based
      // auto-select, which prefers Active and would otherwise hide the draft
      // the user just saved behind another tab.
      // Replace (not push) so the back button on the destination skips
      // over this form — going "back" to a just-saved/empty form is bad UX.
      const stayingAsDraft = !publish && (!isEditMode || isEditingDraft);
      if (stayingAsDraft) {
        history.replace('/my-kitchen?tab=drafts');
      } else {
        ionRouter.push(`/listing/${id}`, 'forward', 'replace');
      }
    } catch (e) {
      console.error('[PublishListing] save failed', e);
      const apiErr = e as Error & { userMessage?: string; errorCode?: string };
      if (apiErr.errorCode === 'COUNTRY_NOT_SUPPORTED') {
        // Defense in depth — the FE's publishBlockers.usOnly check
        // should have prevented submit, but a hand-crafted payload or
        // race with the address picker could still reach the BE guard.
        // Surface the translated message rather than a generic save-fail.
        if (publish) setIsPublishing(false);
        notify.error(apiErr.userMessage ?? t('publishBlockers.usOnly'));
      } else if (apiErr.errorCode === 'LISTING_PRICE_OVER_LOCALE_CAP') {
        // The listing's price exceeds what its area allows. The BE checks
        // this whenever a patch carries BOTH a location and a price, which
        // is every publish — so moving a listing (or a duplicate) into a
        // cheaper/free-only locale lands here if the price came along for
        // the ride. The wheel + clamp normally pull the price down first;
        // this is the "cap wasn't known yet" path. Telling the chef WHY
        // beats a generic retry prompt, since retrying changes nothing.
        if (publish) setIsPublishing(false);
        notify.error(apiErr.userMessage ?? t('errors.priceOverLocaleCap'));
      } else if (apiErr.errorCode === 'LISTING_INCOMPLETE_PUBLISH') {
        // The BE refused to publish a listing that is missing a field a live
        // listing cannot work without. publishBlockers should have caught this
        // first, so reaching here means the form believed it was complete while
        // the payload was not — the exact divergence the publish reconciliation
        // in handleSave closes. Worth its own message: "publish failed, try
        // again" would send the chef round a loop that cannot succeed, and the
        // listing stays a draft (the BE rejects before any write), so their
        // work is intact and re-editing the form is the actual fix.
        if (publish) setIsPublishing(false);
        notify.error(apiErr.userMessage ?? t('errors.incompletePublish'));
      } else if (publish) {
        setIsPublishing(false);
        notify.error(t('errors.publishFailed'));
      } else {
        notify.error(t('errors.saveFailed'));
      }
    }
  }

  /**
   * If the listing location resolves to the same place as the user's profile
   * address, mirror the publish-time apt/unit answer (entered unit OR explicit
   * `noUnit`) back onto the profile so future listings don't re-prompt.
   * Fires only on successful publish (not on draft saves). Best-effort — a
   * failure here doesn't block the publish flow.
   */
  function maybeWriteBackUnitToProfile() {
    if (!activeProfile || !location) return;
    const profileAddr = activeProfile.metadata?.addresses?.find((a) => a.type === 'profile');
    if (!profileAddr) return;
    if (!addressesMatch(profileAddr, location.address)) return;

    const nextStreet2 = location.address.street2;
    const nextNoUnit = !!location.address.noUnit;
    const sameStreet2 = (profileAddr.street2 ?? undefined) === (nextStreet2 ?? undefined);
    const sameNoUnit = !!profileAddr.noUnit === nextNoUnit;
    if (sameStreet2 && sameNoUnit) return;

    updateProfileAddress.mutate(
      {
        profileId: activeProfile.id,
        address: {
          ...profileAddr,
          street2: nextStreet2,
          noUnit: nextNoUnit,
          type: 'profile',
        },
      },
      {
        onError: (e) => console.error('[PublishListing] profile apt write-back failed', e),
      },
    );
  }

  return (
    <IonPage>
      <TopBar />
      {!!editId && isDraftLoading && (
        <div className="fixed inset-0 z-50 flex flex-col items-center justify-center gap-3 bg-background/80">
          <IonSpinner name="crescent" className="h-8 w-8" />
          <span className="text-sm text-muted-foreground">{t('draft.loading')}</span>
        </div>
      )}
      <IonContent className="ion-padding">
        <div className="mx-auto flex w-full max-w-md flex-col gap-10 py-4">
          {resumableDraft && !draftBannerDismissed && title === '' && (
            <div className="flex flex-col gap-3 rounded-lg border border-border bg-card p-4">
              <div className="flex flex-col gap-1">
                <p className="text-sm font-medium text-foreground">{t('draft.resumeTitle')}</p>
                <p className="text-sm text-muted-foreground">
                  {resumableDraft.title?.trim() || t('draft.untitledDraft')}
                </p>
              </div>
              <div className="flex gap-2">
                <IonButton
                  size="small"
                  onClick={() => history.replace(`/publish-listing/${resumableDraft.id}`)}
                >
                  {t('draft.resumeCta')}
                </IonButton>
                <IonButton
                  size="small"
                  fill="clear"
                  onClick={handleDismissDraftBanner}
                >
                  {t('draft.startFresh')}
                </IonButton>
              </div>
            </div>
          )}
          {/* ----- Duplicate banner ----- */}
          {/* The form looks identical in relist mode, so without this the chef
              has no way to tell that saving creates a NEW listing and leaves
              the original alone — they just see their old dish with today's
              date pre-filled. Says "duplicate" because that is literally what
              the save does (POST, not PATCH). */}
          {isRelisting && (
            <div className="rounded-lg border border-border bg-card p-3 text-sm">
              <p className="font-medium text-foreground">{t('banner.duplicateTitle')}</p>
              <p className="mt-1 text-muted-foreground">
                {t('banner.duplicateBody', { title: existingListing?.title?.trim() || t('draft.untitledDraft') })}
              </p>
            </div>
          )}

          {/* ----- Edit-with-orders banner ----- */}
          {isEditWithOrders && (
            <div className="rounded-lg border border-border bg-card p-3 text-sm">
              <p className="font-medium text-foreground">
                {t('banner.editWithOrders', { count: activeOrderCount })}
              </p>
            </div>
          )}

          {/* ----- Title ----- */}
          <LockedFieldOverlay locked={isEditWithOrders} onLockTap={handleLockedTap}>
            <section className="flex flex-col gap-3">
              <h3 className="text-lg font-medium">{t('section.title')}</h3>
              <IonInput
                fill="outline"
                label={t('title.label')}
                labelPlacement="stacked"
                placeholder={t('title.placeholder')}
                value={title}
                onIonInput={(e) => setTitle(e.detail.value ?? '')}
                onIonBlur={handleTitleBlur}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    (document.activeElement as HTMLElement | null)?.blur();
                  }
                }}
              />
            </section>
          </LockedFieldOverlay>

          {/* ----- Description + Tags ----- */}
          <LockedFieldOverlay locked={isEditWithOrders} onLockTap={handleLockedTap}>
          <section className="flex flex-col gap-4">
            <h3 className="text-lg font-medium">{t('section.contents')}</h3>

            {/* Description */}
            <div className="relative">
              {aiGenerating.has('description') && (
                <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 rounded-lg bg-white/70">
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <IonSpinner name="crescent" className="h-4 w-4" />
                    <span>{t('ai.writingDescription')}</span>
                  </div>
                  <IonButton size="small" fill="clear" onClick={cancelAiGeneration}>
                    {t('common:actions.cancel')}
                  </IonButton>
                </div>
              )}
              <IonTextarea
                fill="outline"
                label={t('description.label')}
                labelPlacement="stacked"
                placeholder={t('description.placeholder')}
                autoGrow
                rows={4}
                value={description}
                disabled={aiGenerating.has('description')}
                onIonInput={(e) => setDescription(e.detail.value ?? '')}
                onIonBlur={handleDescriptionBlur}
              />
            </div>

            {/* Spiciness + Tags */}
            <div className="relative">
              {aiGenerating.has('tags') && (
                <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 rounded-lg bg-white/70">
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <IonSpinner name="crescent" className="h-4 w-4" />
                    <span>{t('ai.generatingTags')}</span>
                  </div>
                  <IonButton size="small" fill="clear" onClick={cancelAiGeneration}>
                    {t('common:actions.cancel')}
                  </IonButton>
                </div>
              )}
              <div className="flex flex-col gap-4">
                {/* Spiciness */}
                <div className="flex flex-col gap-2">
                  <h3 className="text-lg font-medium">{t('section.spicy')}</h3>
                  {/* Custom rem-based segmented control (replaces IonSegment,
                      whose shadow-DOM CSS sizes its text independently of the
                      page's rem scale, so it didn't grow with large text).
                      Single-select heat level; now scales with the rest of the
                      form. */}
                  <div className="flex w-full rounded-full border border-border bg-muted/50 p-1">
                    {SPICINESS_LEVELS.map((level) => {
                      const selected = spiciness === level;
                      return (
                        <button
                          key={level}
                          type="button"
                          disabled={aiGenerating.has('tags')}
                          aria-pressed={selected}
                          onClick={() => {
                            setSpiciness(level);
                            savePatch({ spiciness: level });
                          }}
                          className={[
                            'flex-1 rounded-full px-2 py-2 text-base font-medium leading-none transition-colors',
                            selected ? 'bg-brand-primary text-white shadow-sm' : 'text-foreground',
                            aiGenerating.has('tags') ? 'cursor-not-allowed opacity-50' : '',
                          ].join(' ')}
                        >
                          {level === 0 ? t('spice.no') : '🌶️'.repeat(level)}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Dietary */}
                <div className="flex flex-col gap-2">
                  <h3 className="text-lg font-medium">{t('section.dietary')}</h3>
                  <div className="flex flex-wrap gap-2">
                    {mergeTagsForPublish(tagsRegistry?.dietary, dietaryTags).map((tag) => (
                      <SelectableChip
                        key={tag.value}
                        active={dietaryTags.includes(tag.value)}
                        disabled={aiGenerating.has('tags')}
                        onClick={() => toggleTag(tag.value, dietaryTags, setDietaryTags, 'dietaryTags')}
                      >
                        {tag.label}
                      </SelectableChip>
                    ))}
                  </div>
                </div>

                {/* Origins */}
                <div className="flex flex-col gap-2">
                  <h3 className="text-lg font-medium">{t('section.origin')}</h3>
                  <div className="flex flex-wrap gap-2">
                    {mergeTagsForPublish(tagsRegistry?.origins, originTags).map((tag) => (
                      <SelectableChip
                        key={tag.value}
                        active={originTags.includes(tag.value)}
                        disabled={aiGenerating.has('tags')}
                        onClick={() => toggleTag(tag.value, originTags, setOriginTags, 'originTags')}
                      >
                        {tag.label}
                      </SelectableChip>
                    ))}
                  </div>
                </div>

                {/* Allergens */}
                <div className="flex flex-col gap-2">
                  <h3 className="text-lg font-medium">{t('section.allergens')}</h3>
                  <div className="flex flex-wrap gap-2">
                    {mergeTagsForPublish(tagsRegistry?.allergens, allergenTags).map((tag) => (
                      <SelectableChip
                        key={tag.value}
                        active={allergenTags.includes(tag.value)}
                        disabled={aiGenerating.has('tags')}
                        onClick={() => toggleTag(tag.value, allergenTags, setAllergenTags, 'allergenTags')}
                      >
                        {tag.label}
                      </SelectableChip>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>
          </LockedFieldOverlay>

          {/* ----- Image ----- */}
          <section className="flex flex-col gap-4">
            <h3 className="text-lg font-medium">{t('section.image')}</h3>
            <div className="relative flex flex-col gap-2">
              {selectedThumbnailUrl || selectedImageUrl ? (
                <div className="relative">
                  <div className="aspect-[4/3] w-full overflow-hidden rounded-lg" style={{ backgroundColor: 'var(--ion-color-light)' }}>
                    <img
                      src={selectedThumbnailUrl ?? selectedImageUrl!}
                      alt={t('image.altSelected')}
                      className="h-full w-full object-cover"
                    />
                  </div>
                  {aiGenerating.has('image') && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 rounded-lg bg-black/40">
                      <div className="flex items-center gap-2 text-sm text-white">
                        <IonSpinner name="crescent" className="h-4 w-4" />
                        <span>{t('ai.pickingImage')}</span>
                      </div>
                      <IonButton size="small" fill="clear" onClick={dismissAiImage}>
                        {t('common:actions.cancel')}
                      </IonButton>
                    </div>
                  )}
                  {uploadImageMutation.isPending && (
                    <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-black/40 text-sm text-white">
                      {t('image.uploading')}
                    </div>
                  )}
                  {!aiGenerating.has('image') && (
                    <div className="flex gap-2 pt-1">
                      <IonButton fill="outline" size="small" onClick={() => fileInputRef.current?.click()}>
                        {t('image.uploadNew')}
                      </IonButton>
                      <IonButton fill="outline" size="small" onClick={() => setShowImageLibrary(true)}>
                        {t('image.choosePreset')}
                      </IonButton>
                    </div>
                  )}
                </div>
              ) : aiGenerating.has('image') ? (
                <div className="flex h-40 flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-gray-300 bg-white/70">
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <IonSpinner name="crescent" className="h-4 w-4" />
                    <span>{t('ai.pickingImage')}</span>
                  </div>
                  <IonButton size="small" fill="clear" onClick={dismissAiImage}>
                    {t('common:actions.cancel')}
                  </IonButton>
                </div>
              ) : (
                <div className="flex h-40 flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed border-gray-300">
                  {uploadImageMutation.isPending ? (
                    <p className="text-sm text-gray-500">{t('image.uploading')}</p>
                  ) : (
                    <>
                      <IonButton fill="outline" size="small" onClick={() => fileInputRef.current?.click()}>
                        {t('image.uploadAPhoto')}
                      </IonButton>
                      <span className="text-xs text-gray-400">{t('image.or')}</span>
                      <IonButton fill="clear" size="small" onClick={() => setShowImageLibrary(true)}>
                        {t('image.chooseFromPresets')}
                      </IonButton>
                    </>
                  )}
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleImageUpload}
              />
            </div>

            {/* Image library modal */}
            <ImageLibraryModal
              isOpen={showImageLibrary}
              onClose={() => setShowImageLibrary(false)}
              currentImageUrl={selectedImageUrl}
              onSelect={(img) => {
                userChoseImageRef.current = true;
                setSelectedImageUrl(img.imageUrl);
                setSelectedThumbnailUrl(img.thumbnailUrl || img.imageUrl);
                savePatch({
                  imageUrl: img.imageUrl,
                  thumbnailUrl: img.thumbnailUrl || img.imageUrl,
                });
              }}
            />
          </section>

          {/* ----- Price and portions ----- */}
          <section className="flex flex-col gap-4">
            <h3 className="text-lg font-medium">{t('section.price')}</h3>

            <div className="flex justify-evenly">
              <LockedFieldOverlay locked={isEditWithOrders} onLockTap={handleLockedTap}>
                <div className="flex flex-col items-center gap-1">
                  <IonLabel className="text-sm font-medium">{t('price.label')}</IonLabel>
                  <PricePicker value={Math.min(price, maxPriceDollars)} onChange={handlePriceChange} freeLabel={t('price.free')} maxDollars={maxPriceDollars} />
                </div>
              </LockedFieldOverlay>
              <div className="flex flex-col items-center gap-1">
                <IonLabel className="text-sm font-medium">{t('quantity.label')}</IonLabel>
                <QuantityPicker
                  value={Math.max(quantity, portionsSold || 1)}
                  onChange={handleQuantityChange}
                  min={isEditWithOrders ? portionsSold : 1}
                />
              </div>
            </div>

            {/* Blue Star Chef info sheet */}
            <IonModal
              isOpen={showBlueStarSheet}
              initialBreakpoint={0.6}
              breakpoints={[0, 0.6, 0.95]}
              onDidDismiss={() => setShowBlueStarSheet(false)}
            >
              {/* IonContent makes the body scrollable so the content (and the
                  "Got it" button) is always reachable at large text sizes,
                  instead of being clipped by the fixed sheet height; the 0.95
                  breakpoint also lets the user drag the sheet taller. */}
              <IonContent>
                <div className="flex flex-col items-center gap-4 p-6 text-center">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-100 text-2xl">
                    &#9733;
                  </div>
                  <h2 className="text-lg font-semibold">{t('blueStar.title')}</h2>
                  <p className="text-sm text-gray-600">
                    {t('blueStar.tagline')}
                  </p>
                  <p className="text-sm text-gray-500">
                    {t('blueStar.body')}
                  </p>
                  <div className="flex w-full gap-3 pt-2">
                    <IonButton expand="block" className="flex-1" onClick={() => setShowBlueStarSheet(false)}>
                      {t('blueStar.gotIt')}
                    </IonButton>
                  </div>
                </div>
              </IonContent>
            </IonModal>

            {/* Over-cap price info sheet — shown when the user settles on a
                grayed price above the locale's cap. */}
            <IonModal
              isOpen={showLockedPriceSheet}
              initialBreakpoint={0.6}
              breakpoints={[0, 0.6, 0.95]}
              onDidDismiss={() => setShowLockedPriceSheet(false)}
            >
              {/* Scrollable + draggable-taller, same as the Blue Star sheet, so
                  large text never clips the content or the "Got it" button. */}
              <IonContent>
                <div className="flex flex-col items-center gap-4 p-6 text-center">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted text-2xl">
                    &#128274;
                  </div>
                  <h2 className="text-lg font-semibold">{t('lockedPrice.title')}</h2>
                  <p className="text-sm text-gray-600">
                    {location?.address.city && location?.address.stateProvince
                      ? t('lockedPrice.bodyWithLocation', {
                          city: location.address.city,
                          st: location.address.stateProvince,
                        })
                      : t('lockedPrice.body')}
                  </p>
                  {(listingInOtherDishLocale || listingLocationOutsideDishLocale) && (
                    <p className="text-sm text-gray-600">
                      {t('lockedPrice.outsideLocaleNote')}
                    </p>
                  )}
                  <div className="flex w-full gap-3 pt-2">
                    <IonButton expand="block" className="flex-1" onClick={() => setShowLockedPriceSheet(false)}>
                      {t('lockedPrice.gotIt')}
                    </IonButton>
                  </div>
                </div>
              </IonContent>
            </IonModal>

            {/* Feed Your Community info sheet */}
            <IonModal
              isOpen={showFeedCommunitySheet}
              initialBreakpoint={0.6}
              breakpoints={[0, 0.6, 0.95]}
              onDidDismiss={() => setShowFeedCommunitySheet(false)}
            >
              {/* Scrollable + draggable-taller, same as the Blue Star sheet, so
                  large text never clips the content or the "Got it" button. */}
              <IonContent>
                <div className="flex flex-col items-center gap-4 p-6 text-center">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-orange-100 text-2xl">
                    &#127858;
                  </div>
                  <h2 className="text-lg font-semibold">{t('feedCommunity.title')}</h2>
                  <p className="text-sm text-gray-600">
                    {t('feedCommunity.tagline')}
                  </p>
                  <p className="text-sm text-gray-500">
                    {t('feedCommunity.body')}
                  </p>
                  <div className="flex w-full gap-3 pt-2">
                    <IonButton expand="block" className="flex-1" onClick={() => setShowFeedCommunitySheet(false)}>
                      {t('feedCommunity.gotIt')}
                    </IonButton>
                  </div>
                </div>
              </IonContent>
            </IonModal>

            {/* First-listing free-dish nudge. Purely presentational — dismissing
                it leaves the chosen price untouched. */}
            <IonModal
              isOpen={showFirstPriceNudge}
              initialBreakpoint={0.6}
              breakpoints={[0, 0.6, 0.95]}
              onDidDismiss={() => setShowFirstPriceNudge(false)}
            >
              <IonContent>
                <div className="flex flex-col items-center gap-4 p-6 text-center">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted text-2xl">
                    &#127793;
                  </div>
                  <h2 className="text-lg font-semibold text-foreground">{t('firstPriceNudge.title')}</h2>
                  <p className="text-sm text-muted-foreground">
                    {t('firstPriceNudge.body')}
                  </p>
                  <div className="flex w-full gap-3 pt-2">
                    <IonButton expand="block" className="flex-1" onClick={() => setShowFirstPriceNudge(false)}>
                      {t('firstPriceNudge.gotIt')}
                    </IonButton>
                  </div>
                </div>
              </IonContent>
            </IonModal>
          </section>

          {/* ----- Hand-off options & windows ----- */}
          <LockedFieldOverlay locked={isEditWithOrders} onLockTap={handleLockedTap}>
          <section className="flex flex-col gap-4">
            <h3 className="text-lg font-medium">{t('section.handoff')}</h3>

            {/* Methods come first because the windows below are per-method:
                which cards exist is a consequence of what's checked here.
                Inner lock only when the section-wide one above isn't already
                covering it, so a tap isn't handled twice. */}
            <LockedFieldOverlay
              locked={handoffMethodsLocked && !isEditWithOrders}
              onLockTap={handleMethodsLockedTap}
            >
            <div className="overflow-hidden rounded-xl border border-border">
              {(['pickup', 'dropoff'] as const).map((kind, i) => {
                const checked = fulfillment.includes(kind);
                return (
                  <button
                    key={kind}
                    type="button"
                    role="checkbox"
                    aria-checked={checked}
                    onClick={() => handleToggleHandoff(kind)}
                    className={`flex w-full items-center gap-3 px-4 py-3 text-left ${
                      i > 0 ? 'border-t border-border' : ''
                    }`}
                  >
                    <span
                      aria-hidden
                      className={`flex h-5 w-5 shrink-0 items-center justify-center rounded ${
                        checked
                          ? 'bg-brand-primary text-white'
                          : 'border border-border'
                      }`}
                    >
                      {checked && <Check size={14} />}
                    </span>
                    <span className="text-base text-foreground">{t(`handoff.${kind}`)}</span>
                  </button>
                );
              })}
            </div>
            </LockedFieldOverlay>

            {/* Chronological, not a fixed pickup-then-drop-off order: checking
                drop-off first leaves it as the earlier window, and a card stack
                reading 7:00 PM above 6:30 PM is just wrong. Keyed by kind so a
                card keeps its identity (and its open sheet) when the order
                flips under an edit. */}
            {handoffCards.map(({ kind, window, bounds, other }) => (
              <HandoffWindowCard
                key={kind}
                kind={kind}
                window={window}
                bounds={bounds}
                original={originalWindow.current[kind]}
                otherWindow={other}
                locale={i18n.language || 'en'}
                onChange={(next) => commitWindow(kind, next)}
              />
            ))}
            {/* Radius and fee sit below BOTH cards rather than inside the
                drop-off one: the cards are ordered chronologically, so "inside
                the drop-off card" moves around, and these two aren't times. */}
            {offersDropoff ? (
              <div className="flex flex-col gap-2">
                <h4 className="text-base font-medium">{t('handoff.dropoffDetails')}</h4>
                <div className="flex justify-evenly">
                  <div className="flex flex-col items-center gap-1">
                    <IonLabel className="text-sm font-medium">{t('handoff.radiusLabel')}</IonLabel>
                    <OptionWheel
                      value={dropoffRadius}
                      options={DROPOFF_RADIUS_OPTIONS.map((v) => ({ value: v, label: formatRadiusLabel(v) }))}
                      suffix={t('handoff.radiusSuffix')}
                      onChange={handleDropoffRadiusChange}
                    />
                  </div>
                  <div className="flex flex-col items-center gap-1">
                    <IonLabel className="text-sm font-medium">{t('handoff.feeLabel')}</IonLabel>
                    <OptionWheel
                      value={dropoffFeeDollars}
                      options={DROPOFF_FEE_OPTIONS.map((v) => ({
                        value: v,
                        label: v === 0 ? t('handoff.feeFree') : `$${v}`,
                      }))}
                      onChange={handleDropoffFeeChange}
                    />
                  </div>
                </div>
                <p className="text-sm text-muted-foreground">{t('handoff.feeHint')}</p>
              </div>
            ) : null}
          </section>
          </LockedFieldOverlay>

          {/* ----- Location ----- */}
          <LockedFieldOverlay locked={isEditWithOrders} onLockTap={handleLockedTap}>
          <section className="flex flex-col gap-4">
            {/* Location */}
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between gap-2">
                <h3 className="text-lg font-medium">{t('section.location')}</h3>
                <button
                  type="button"
                  onClick={handleUseCurrentLocation}
                  disabled={geoLoading || !locationReady}
                  className="inline-flex items-center gap-1.5 text-sm disabled:opacity-50"
                  style={{ color: 'var(--ion-color-primary)' }}
                >
                  <Locate size={16} />
                  {geoLoading ? t('location.gettingLocation') : t('location.useCurrentCta')}
                </button>
              </div>
              {geoError && (
                <p
                  className="text-xs"
                  style={{ color: 'var(--ion-color-danger)' }}
                >
                  {geoError}
                </p>
              )}
              {locationReady && (
                <LocationPicker
                  ref={locationPickerRef}
                  value={location}
                  // Shows the chef the area they're agreeing to drive.
                  radiusMiles={offersDropoff ? dropoffRadius : null}
                  profileAddress={activeProfile?.metadata?.addresses?.find((a) => a.type === 'profile') ?? null}
                  onAptResolutionChange={setAptResolved}
                  onPinResolutionChange={setPinResolved}
                  onChange={(loc) => {
                    setLocation(loc);
                    const addr = loc.address;
                    const fields: PatchListingRequest = {
                      listingLocation: {
                        lat: addr.lat,
                        lon: addr.lon,
                        street1: addr.street1,
                        street2: addr.street2,
                        city: addr.city,
                        stateProvince: addr.stateProvince,
                        postalCode: addr.postalCode,
                        country: addr.country,
                        placeId: addr.placeId,
                        noUnit: addr.noUnit,
                        pinConfirmed: addr.pinConfirmed,
                      },
                      postalCode: addr.postalCode,
                      stateProvince: addr.stateProvince,
                    };
                    // Re-stamp both windows' timezone to match the new
                    // location. `listingTimezone` (memo) is still stale in this
                    // closure since setLocation hasn't flushed, so derive from
                    // the incoming coords directly rather than going through
                    // stageWindows, which reads the stale memo. Only overwrite
                    // when the lookup resolves — otherwise leave whatever's
                    // there (the on-mount browser fallback).
                    const tz = deriveTimezone(addr.lat, addr.lon);
                    if (tz) {
                      const restamp = (w: unknown) => {
                        const win = w as Record<string, unknown> | undefined;
                        // An unoffered method stages as `{}`; re-stamping it
                        // would turn "clear this window" into a partial window.
                        return win && win.start ? { ...win, timezone: tz } : win;
                      };
                      const nextPickup = restamp(accumulatedFields.current.pickupWindow);
                      const nextDropoff = restamp(accumulatedFields.current.dropoffWindow);
                      accumulatedFields.current = {
                        ...accumulatedFields.current,
                        pickupWindow: nextPickup,
                        dropoffWindow: nextDropoff,
                      };
                      fields.pickupWindow = nextPickup;
                      fields.dropoffWindow = nextDropoff;
                    }
                    savePatch(fields);
                  }}
                />
              )}
              {listingLat != null && listingLon != null && (
                <p className="text-xs" style={{ color: 'var(--ion-color-medium)' }}>
                  {t('location.timezoneLabel')}: {listingTimezone}
                </p>
              )}
              {(listingInOtherDishLocale || listingLocationOutsideDishLocale) && (
                <div
                  className="flex flex-col gap-2 rounded-lg border p-3"
                  style={{
                    backgroundColor: 'var(--ion-color-warning-tint)',
                    borderColor: 'var(--ion-color-warning-shade)',
                    color: 'var(--ion-color-warning-contrast)',
                  }}
                >
                  <p className="text-sm font-medium">
                    {t('location.outsideCurrentLocale')}
                  </p>
                  {listingInOtherDishLocale && listingLocale ? (
                    <button
                      type="button"
                      onClick={() => setLocale(listingLocale)}
                      className="self-start rounded-lg border px-3 py-1.5 text-sm font-medium"
                      style={{
                        backgroundColor: 'var(--ion-background-color)',
                        borderColor: 'var(--ion-color-medium-tint)',
                        color: 'var(--ion-text-color)',
                      }}
                    >
                      {t('location.changeLocaleCta', {
                        locale:
                          listingLocale.name ||
                          [location?.address.city, location?.address.stateProvince]
                            .filter(Boolean)
                            .join(', '),
                      })}
                    </button>
                  ) : (
                    <p className="text-sm">
                      {t('location.notAvailableHere', {
                        location: [
                          location?.address.city,
                          location?.address.stateProvince,
                        ]
                          .filter(Boolean)
                          .join(', '),
                      })}
                    </p>
                  )}
                </div>
              )}
              {needsListingPinAck && !listingPinAcknowledged && (
                <div
                  className="flex flex-col gap-2 rounded-lg border p-3"
                  style={{
                    backgroundColor: 'var(--ion-color-warning-tint)',
                    borderColor: 'var(--ion-color-warning-shade)',
                    color: 'var(--ion-color-warning-contrast)',
                  }}
                >
                  <p className="text-sm font-medium">
                    {t('location.pinAckPrompt')}
                  </p>
                  <button
                    type="button"
                    onClick={() => setListingPinAcknowledged(true)}
                    className="self-start rounded-lg border px-3 py-1.5 text-sm font-medium"
                    style={{
                      backgroundColor: 'var(--ion-background-color)',
                      borderColor: 'var(--ion-color-medium-tint)',
                      color: 'var(--ion-text-color)',
                    }}
                  >
                    {t('location.pinAckCta')}
                  </button>
                </div>
              )}
            </div>
          </section>
          </LockedFieldOverlay>

          {/* ----- Visibility (per-dish override) ----- */}
          {/* First-timers set this via the publish prompt instead; returning
              chefs override their default per dish here. */}
          {hasPublishedBefore && (
            <LockedFieldOverlay locked={isEditWithOrders} onLockTap={handleLockedTap}>
              <section className="space-y-2">
                <h3 className="text-lg font-medium">{t('visibility.title')}</h3>
                <p className="text-sm text-muted-foreground">{t('visibility.description')}</p>
                <ShareLevelDropdown
                  value={shareLevel}
                  onChange={(level) => {
                    setShareLevel(level);
                    savePatch({ shareConnectionLevel: level });
                  }}
                  maxLevel={listingLocale?.maxShareConnectionLevel}
                />
              </section>
            </LockedFieldOverlay>
          )}

          {/* ----- Publish blockers ----- */}
          {/* Renders every reason the Publish button is disabled. Driven by the
              same `disableReasons` the button's `disabled` uses, so the button
              can never gray out without a matching explanation here. */}
          {disableReasons.length > 0 && (
            <div className="rounded-lg border border-border bg-card p-3 text-sm">
              <p className="font-medium text-foreground">{t('publishBlockers.title')}</p>
              <ul className="mt-1 list-inside list-disc text-muted-foreground">
                {disableReasons.map((b) => (
                  <li key={b}>{b}</li>
                ))}
              </ul>
            </div>
          )}

          {/* ----- Footer actions ----- */}
          {/* Consistent button styling + a layout that can't overflow: the
              secondary actions (Save Draft / Delete-or-Cancel) share one
              wrapping row, and the primary action (Publish) is full-width below.
              On narrow screens or large text the secondaries stack instead of
              being pushed off-screen. */}
          <div className="flex flex-col gap-2 pb-8">
            {(!isEditMode || isEditingDraft || canCancelListing) && (
              <div className="flex flex-wrap gap-2">
                {/* Save Draft is backend-only — hidden for logged-out users
                    (their dish auto-buffers to localStorage; there's no draft to
                    save until they have an account). */}
                {isAuthenticated && (!isEditMode || isEditingDraft) && (
                  <button
                    type="button"
                    disabled={!listingId.current}
                    onClick={() => handleSave({ publish: false })}
                    className="flex-1 min-w-[8rem] rounded-xl bg-secondary py-3 text-base font-semibold text-foreground transition-colors active:opacity-80 disabled:opacity-50"
                  >
                    {t('actions.saveDraft')}
                  </button>
                )}
                {(canCancelListing || !isEditMode) && (
                  isOperatingOnDraft ? (
                    <button
                      type="button"
                      onClick={() => setShowDeleteModal(true)}
                      className="flex-1 min-w-[8rem] rounded-xl bg-secondary py-3 text-base font-semibold text-brand-danger transition-colors active:opacity-80"
                    >
                      {t('common:actions.delete')}
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => {
                        // New listing with no backend draft yet: nothing was
                        // auto-drafted, so just bounce back to the home feed
                        // (the only entry to a no-id /publish-listing is the
                        // HomePage fab). Cancelling here also discards a
                        // logged-out buffered dish — the user chose not to keep
                        // it, so it shouldn't be remembered/restored next time.
                        // (No-op for authed users, who never write a buffer.)
                        if (!isEditMode && !listingId.current) {
                          clearPendingListing();
                          history.replace('/');
                          return;
                        }
                        setShowCancelModal(true);
                      }}
                      className="flex-1 min-w-[8rem] rounded-xl bg-secondary py-3 text-base font-semibold text-brand-danger transition-colors active:opacity-80"
                    >
                      {t('common:actions.cancel')}
                    </button>
                  )
                )}
              </div>
            )}
            <button
              type="button"
              // Enabled purely on a valid dish (publishBlockers) — we don't
              // require a backend draft to already exist, because handleSave's
              // ensureCreated makes one at publish time. Requiring listingId
              // here would wedge the button for a user who came through the
              // logged-out flow (title blurred pre-signup, so no draft) and then
              // backed out of the chef quiz — they'd be authed with no draft and
              // no way to re-enable Publish.
              disabled={isPublishing || disableReasons.length > 0}
              onClick={() => handleSave({ publish: isPublishingAttempt })}
              className="flex w-full items-center justify-center rounded-xl bg-brand-primary py-3 text-base font-semibold text-white shadow-sm transition active:opacity-80 disabled:opacity-50"
            >
              {isPublishing ? (
                <IonSpinner name="dots" />
              ) : isRelisting ? (
                // Names the outcome on the button itself: this publishes a new
                // listing and leaves the source untouched.
                t('actions.publishDuplicate')
              ) : !isEditMode || isEditingDraft ? (
                t('actions.publish')
              ) : (
                t('actions.updateListing')
              )}
            </button>
          </div>
        </div>
      </IonContent>
      <CancelListingModal
        isOpen={showCancelModal}
        onClose={handleCloseCancelModal}
        onConfirm={handleConfirmCancel}
        isPending={cancelMutation.isPending}
        error={cancelError}
        requireReason
        // For the cancel-and-copy variant, we feed our own translated copy
        // (the body needs the live activeOrderCount). For the plain cancel
        // variant, the modal's defaults render through publish.cancelModal.*
        // — we leave title/description/labels undefined so it uses them.
        title={cancelMode === 'cancel-and-copy' ? t('cancelModal.titleCancelAndCopy') : undefined}
        description={
          cancelMode === 'cancel-and-copy'
            ? t('cancelModal.bodyCancelAndCopy', { count: activeOrderCount })
            : undefined
        }
        confirmLabel={cancelMode === 'cancel-and-copy' ? t('cancelModal.confirmCancelAndCopy') : undefined}
        cancelLabel={cancelMode === 'cancel-and-copy' ? t('cancelModal.cancelCancelAndCopy') : undefined}
      />
      <CancelListingModal
        isOpen={showDeleteModal}
        onClose={handleCloseDeleteModal}
        onConfirm={handleConfirmDelete}
        isPending={deleteMutation.isPending}
        error={deleteError}
        title={t('deleteModal.title')}
        description={t('deleteModal.body')}
        confirmLabel={t('deleteModal.confirm')}
        cancelLabel={t('deleteModal.cancel')}
      />
      <EditLockedModal
        isOpen={showEditLockedModal}
        onClose={() => setShowEditLockedModal(false)}
        onDeleteAndCopy={handleDeleteAndCopy}
        onCopyKeepActive={handleCopyKeepActive}
        reason={editLockedReason}
      />
      <PublishInstructionsModal
        isOpen={showInstructions}
        onClose={() => setShowInstructions(false)}
      />
      <FirstPublishVisibilityModal
        isOpen={visibilityModalOpen}
        defaultLevel={shareLevel}
        isSubmitting={updateReach.isPending || isPublishing}
        onConfirm={handleVisibilityConfirm}
        onClose={() => setVisibilityModalOpen(false)}
        maxLevel={listingLocale?.maxShareConnectionLevel}
      />
      <ProfileLocationModal
        isOpen={addressModalOpen}
        onClose={handleAddressGateDismissed}
        mode="publish"
        dishLocation={hasValidListingLocation ? location : null}
        onSaved={handleAddressSaved}
      />
      <ChefApplicationModal
        isOpen={chefQuizModalOpen}
        onClose={() => setChefQuizModalOpen(false)}
        bodyText={t('becomeChef.gateBody')}
        onCompleted={handleBecameChef}
      />
      <SignInModal
        isOpen={signupGateOpen}
        onClose={() => setSignupGateOpen(false)}
        initialMode="signUp"
        intro={t('loggedOutGate.body')}
      />
    </IonPage>
  );
};
